import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import DrawRectangle from 'mapbox-gl-draw-rectangle-mode';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import percentile from 'percentile';
import { Sidebar } from 'primereact/sidebar';
import { InputSwitch } from 'primereact/inputswitch';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import md5 from 'md5';

import FormFiltersContainer from './FormFiltersContainer';
import Legenda from '../Legenda';
import HeatMapLegend from '../HeatMapLegend';
import HeatMapModal from './HeatMapModal';
import LegendaPoloGerador from '../LegendaPoloGerador';
import PolosGeradoresTypesModal from './PolosGeradoresTypesModal';
import MapboxResultsPanel from './MapboxResultsPanel';
import EmpreendimentoModal from './EmpreendimentoModal';
import RaioDeslocamentoModal from './RaioDeslocamentoModal';
import ModalRanges from '../ModalRanges';
import ModalSalvarProjeto from '../Projeto/ModalSalvarProjeto';
import MapCapture from '../MapCapture/MapCapture';
import DrawnShapesList from './DrawnShapesList';
import ShapeAttributesEditor from './ShapeAttributesEditor';
import { circleTwoClickMode, markerCircleMode, DrawExtraControls, makeCircleFeature, drawStyles } from './drawModes';
import geolocationService from '../../services/geolocationService';
import KmlService from '../../services/KmlService';
import ZoneamentoService from '../../services/ZoneamentoService';
import FiltroZoneamento from '../FiltroZoneamento';
import LegendaDataLayer from '../LegendaDatalayer/Legenda';
// Dados censitários (camadas Linkages/IBGE) — paridade com o mapa Google (DefaulMap.jsx).
import LinkagesDataLayerService from '../../services/LinkagesDataLayer/LinkagesDataLayerService';
import { initializeColors } from '../../services/rendaIbgeService';
import getLabel from '../../services/LinkagesDataLayer/labels';
import formater from '../../services/LinkagesDataLayer/formater';
import LinkagesDataDictionaryModal from '../LinkagesDataModal/LinkagesDataDictionaryModal';
import LinkagesDataTableModal from '../LinkagesDataModal/LinkagesDataTableModal';
import RendaIbgeLegend from '../RendaIbgeLegend';
import ModalLegendaRendaIbge from '../ModalLegendaRendaIbge/ModalLegendaRendaIbge';
import colors from '../../util/colors';
import '../mapa.css';

const BRAZIL_CENTER = [-51.9253, -14.235];
const DEFAULT_ZOOM = 4;
const SELECTED_ZOOM = 12;
const SOURCE_ID = 'empreendimentos';
const BAIRROS_SOURCE_ID = 'bairros';
const HEAT_SOURCE_ID = 'empreendimentos-heat';
const POLOS_SOURCE_ID = 'polos-geradores';
const EMPTY_FC = { type: 'FeatureCollection', features: [] };

// Polos geradores — paridade com o mapa Google. main_category → arquivo de ícone
// (espelha PoloGerador::getImageAttribute e LegendaPoloGerador). Imagens carregadas
// sob o nome `polo_<arquivo>` em /img/polos_geradores/grupos/<arquivo>.png.
const POLO_ICON_BY_CATEGORY = {
    'Alimentação': 'alimentacao',
    'Automotivo': 'automotivo',
    'Decoração': 'decoracao',
    'Educação': 'educacao',
    'Instituição Financeira': 'financeira',
    'Moda': 'moda',
    'Outros': 'outros',
    'Saúde e Bem-Estar': 'saude',
    'Serviços': 'servicos',
    'Shopping Center': 'shopping-center-2',
    'Loja De Materiais De Construção': 'construcao-e-bricolagem',
};
const POLO_ICON_FILES = [...new Set(Object.values(POLO_ICON_BY_CATEGORY))];
const POLOS_LAYERS = ['polos-clusters', 'polos-cluster-count', 'polos-unclustered-point'];

// Mapa de calor — paridade com o mapa Google (DefaulMap.jsx).
// As 8 cores/buckets de percentil e os PNGs em /img/marcadores/mapa_de_calor/.
const HEAT_COLORS = ['194D72', '3D8ABD', 'B1D974', '7CA83E', '5B7537', 'F1A30C', 'B29400', 'C50234'];
const HEAT_ICON_NAMES = HEAT_COLORS.map((hex) => `heat_${hex}`);
const HEAT_PERCENTILES = [12.5, 25, 32.5, 50, 62.5, 75, 87.5];
// Camadas que compõem a exibição padrão de empreendimentos (clusters + marcadores).
const CLUSTER_LAYERS = ['clusters', 'cluster-count', 'unclustered-point'];
// range.type (ModalRanges) → property correspondente no GeoJSON de empreendimentos.
const RANGE_PROPERTY = {
    area_media: 'area_media',
    ticket_medio_metro: 'ticket_medio_metro',
    total_unidades: 'oferta_inicial',
    oferta_final: 'oferta_final',
};
const HEAT_LAYERS = ['heatmap-layer', 'heat-points'];
// Cidades com limites de bairros disponíveis (formato "UF-Cidade"), injetadas no
// page load a partir da tabela cities. Decide a exibição do switch sem AJAX.
const BAIRROS_CITIES = new Set(window.bairrosCities || []);

// Zoneamento — paridade com o mapa Google (DefaulMap.jsx). Camada GeoJSON colorida
// por sigla_zoneamento. As camadas usam um filtro de visibilidade por feature
// (`visible`), alternado pelo FiltroZoneamento.
const ZONEAMENTO_SOURCE_ID = 'zoneamento';
const ZONEAMENTO_LAYERS = ['zoneamento-fill', 'zoneamento-outline'];
const ZONEAMENTO_VISIBLE_FILTER = ['==', ['get', 'visible'], true];
// Permissão `ver-zoneamento` injetada no page load (ver mapbox/index.blade.php).
const CAN_VER_ZONEAMENTO = !!window.can_ver_zoneamento;

// Dados censitários — camada GeoJSON colorida por `color` (definido por initializeColors).
// As permissões por camada (renda, consumo, etc.) vêm de route('user.auth').permissoes_sociodemografia.
const DATALAYER_SOURCE_ID = 'datalayer';
const DATALAYER_LAYERS = ['datalayer-fill', 'datalayer-outline'];
// Propriedades técnicas que não devem aparecer no popup do setor.
const DATALAYER_IGNORE_PROPERTIES = ['dt_upload', 'gid_uf', 'gid_municipio', 'id', 'color'];

const MAP_STYLE_STORAGE_KEY = 'geobrain.mapbox.style';
const MAP_STYLES = {
    light: 'mapbox://styles/mapbox/streets-v12',
    dark:  'mapbox://styles/mapbox/dark-v11',
};
const readStoredMapStyle = () => {
    try {
        const v = localStorage.getItem(MAP_STYLE_STORAGE_KEY);
        return v === 'dark' || v === 'light' ? v : 'light';
    } catch {
        return 'light';
    }
};

const GOOGLE_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
const GOOGLE_SCRIPT_ID = 'google-maps-places-script';

const ICON_LETTERS = ['E', 'O', 'S', 'M', 'A', 'L', 'X', 'H', 'C'];
const ICON_NAMES = [
    ...ICON_LETTERS.flatMap((l) => [`green_Marker${l}`, `red_Marker${l}`]),
    'yellow_MarkerF',
];

function loadGoogleMapsPlaces() {
    if (window.google?.maps?.places) return Promise.resolve();
    if (document.getElementById(GOOGLE_SCRIPT_ID)) {
        return new Promise((resolve) => {
            document.getElementById(GOOGLE_SCRIPT_ID).addEventListener('load', resolve, { once: true });
        });
    }
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.id = GOOGLE_SCRIPT_ID;
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places&language=pt-BR&region=BR`;
        script.async = true;
        script.defer = true;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
    });
}

class MapboxMap extends Component {
    state = {
        left_side_bar_visible: true,
        right_panel_visible: false,
        empreendimentoDetail: null,
        showEmpreendimentoModal: false,
        loadingEmpreendimentoDetail: false,
        active_tab: 'filtros',
        totalEmpreendimentos: 0,
        cidadeSincronizada: null,
        mapStyle: readStoredMapStyle(),
        searchAreas: [],
        showBairros: false,
        bairrosAvailable: false,
        show_map_capture: false,
        mapCaptureData: null,
        mapCaptureCanvas: null,
        drawnShapes: [],
        modalHeatMapOpen: false,
        showHeatMap: false,
        heatMapWeightType: null,
        heatMapLegendConfig: [],
        shapeEditorOpen: false,
        shapeEditing: null,
        showPolosGeradores: false,
        polosModalOpen: false,
        polosTypesSelected: [],
        polosLoaded: false,
        showEmpreendimentos: true,
        // "Incluir cidades não monitoradas" (aba Ferramentas, só usuários internos):
        // por padrão a busca cobre apenas cidades monitoradas (paridade com o mapa Google).
        incluirNaoMonitoradas: false,
        showZoneamento: false,
        zoneamentoAvailable: false,
        legendaZoneamento: [],
        opcoesPropriedadesZoneamento: [],
        valoresPropriedadesZoneamento: {},
        // Dados censitários (camadas Linkages/IBGE).
        permissoesSociodemografia: [],
        datalayers: [],
        datalayerName: '',
        datalayerFriendlyName: null,
        datalayerColorProperty: null,
        datalayerColorPropertyOptions: [],
        datalayerYear: null,
        datalayerYears: [],
        datalayerYearOptions: {},
        datalayerFillOpacity: 0.5,
        showLegendaRenda: false,
        show_demarcacao_areas: false,
        color_ranges: {},
        colorsOptions: [],
        show_modal_datalayer_datatable: false,
        datalayers_table_data: [],
        show_modal_data_dictionary: false,
        show_modal_customizar_cores_setores: false,
        loadingDatalayer: false,
        loadingKml: false,
        // Raio por Deslocamento (isócrona)
        showRaioDeslocamento: false,
        pickingIsochroneCenter: false,
        isochroneCenter: null,
        // Ranges customizados de cor/imagem dos marcadores (ModalRanges)
        ranges: [],
        modalRangesOpen: false,
        // Projetos (salvar / carregar / excluir / compartilhar)
        modalSalvarProjetoOpen: false,
        projectName: '',
        projectShareUsers: [],
        projectErrors: {},
        savingProject: false,
        loadedProjectId: null,
    };

    // Input de arquivo (oculto) para importação de KML/KMZ.
    kmlInputRef = React.createRef();

    // Cache cru das GeoJSONs de dados censitários por hash de payload+camada
    // (espelha datalayers_data_cache do DefaulMap) e o GeoJSON atualmente exibido
    // (para reaplicar após troca de estilo).
    datalayersDataCache = {};
    currentDatalayerGeojson = null;

    // Última config de mapa de calor escolhida no modal (para reaplicar após
    // troca de filtros/estilo). null quando o heatmap está desligado.
    heatMapConfig = null;

    // Cache cru das features de polos geradores (antes do recorte por forma).
    allPolos = [];

    bairrosCache = {};

    // Zoneamento: serviço reutilizado do mapa Google + cache do prepareData por
    // cidade (chave "UF-Cidade") e a cidade atualmente exibida (para o filtro).
    zoneamentoService = new ZoneamentoService();
    zoneamentoCache = {};
    currentZoneamentoCidade = null;

    containerRef = React.createRef();
    searchInputRef = React.createRef();
    formFiltersRef = React.createRef();
    map = null;
    draw = null;
    drawExtras = null;
    currentGeojson = null;
    allFeatures = [];

    componentDidMount() {
        const apiKey = window.mapboxApiKey;
        if (!apiKey || !this.containerRef.current) {
            if (!apiKey) console.error('[MapboxMap] window.mapboxApiKey ausente');
            return;
        }

        mapboxgl.accessToken = apiKey;
        this.map = new mapboxgl.Map({
            container: this.containerRef.current,
            style: MAP_STYLES[this.state.mapStyle],
            center: BRAZIL_CENTER,
            zoom: DEFAULT_ZOOM,
            preserveDrawingBuffer: true, // necessário para getCanvas().toDataURL() na captura de tela
        });

        this.map.on('error', (e) => console.error('[MapboxMap] erro:', e.error));

        this.map.addControl(new mapboxgl.NavigationControl(), 'top-right');
        this.map.addControl(new mapboxgl.FullscreenControl(), 'top-right');
        this.map.addControl(
            new mapboxgl.GeolocateControl({
                positionOptions: { enableHighAccuracy: true },
                trackUserLocation: true,
            }),
            'top-right'
        );

        this.setupDrawTools();

        this.map.on('load', () => this.initializeLayers());

        this.setupGooglePlaces();
        this.loadSociodemografiaPermissions();
    }

    // Carrega as permissões de sociodemografia do usuário e monta as opções de
    // "Dados censitários" filtradas por permissão (espelha Ferramentas.jsx).
    loadSociodemografiaPermissions = async () => {
        try {
            const response = await axios.get(route('user.auth'));
            const permissoes = response.data?.permissoes_sociodemografia || [];

            const requiredPermissions = LinkagesDataLayerService.getRequiredPermissionsMap();
            const datalayers = LinkagesDataLayerService.getAvailableLayers()
                .map((layerGroup) => ({
                    ...layerGroup,
                    options: layerGroup.options.filter(
                        (option) => permissoes.includes(requiredPermissions[option.value])
                    ),
                }))
                .filter((layerGroup) => layerGroup.options.length > 0);

            this.setState({ permissoesSociodemografia: permissoes, datalayers });
        } catch (e) {
            console.error('[MapboxMap] erro ao carregar permissões de sociodemografia:', e);
        }
    };

    setupDrawTools = () => {
        this.draw = new MapboxDraw({
            displayControlsDefault: false,
            controls: { polygon: true, trash: true },
            // Necessário para os estilos lerem os atributos da feature via `user_*`
            // (cor/opacidade/borda por forma — ver drawStyles).
            userProperties: true,
            styles: drawStyles,
            modes: {
                ...MapboxDraw.modes,
                draw_rectangle: DrawRectangle,
                draw_circle_two_click: circleTwoClickMode,
                draw_marker_circle: markerCircleMode,
            },
        });
        this.map.addControl(this.draw, 'top-right');

        this.drawExtras = new DrawExtraControls({ draw: this.draw });
        this.map.addControl(this.drawExtras, 'top-right');

        this.map.on('draw.create', this.handleDrawCreate);
        this.map.on('draw.update', this.handleDrawUpdate);
        this.map.on('draw.delete', this.handleDrawDelete);
    };

    applyShapeFilter = () => {
        if (!this.map?.getSource(SOURCE_ID)) return;

        const shapes = (this.draw?.getAll().features || []).filter(
            (f) => f.geometry?.type === 'Polygon' || f.geometry?.type === 'MultiPolygon'
        );

        let filtered = this.allFeatures;
        if (shapes.length > 0) {
            filtered = this.allFeatures.filter((point) =>
                shapes.some((shape) => {
                    try {
                        return booleanPointInPolygon(point, shape);
                    } catch {
                        return false;
                    }
                })
            );
        }

        const geojson = { type: 'FeatureCollection', features: filtered };
        this.currentGeojson = geojson;
        this.map.getSource(SOURCE_ID).setData(geojson);
        this.setState({ totalEmpreendimentos: filtered.length });

        // Se o mapa de calor está ativo, re-deriva a camada de calor a partir dos
        // empreendimentos atualmente filtrados.
        if (this.state.showHeatMap && this.heatMapConfig) {
            this.addHeatMap(this.heatMapConfig);
        }

        // Recorta os polos geradores pelas mesmas formas desenhadas.
        if (this.state.showPolosGeradores && this.allPolos.length) {
            this.applyPolosShapeFilter();
        }
    };

    handleDrawCreate = (e) => {
        this.applyShapeFilter();

        const newShape = e.features?.[0];
        if (!newShape || (newShape.geometry?.type !== 'Polygon' && newShape.geometry?.type !== 'MultiPolygon')) return;

        const shapeType = newShape.properties?.shapeType;
        const typeLabels = { circle: 'Círculo', marker_circle: 'Círculo', rectangle: 'Retângulo' };
        const type = typeLabels[shapeType] || 'Polígono';
        const name = `${type} ${this.state.drawnShapes.length + 1}`;
        const color = colors[this.state.drawnShapes.length % colors.length];
        const areaId = `drawn-${Date.now()}`;

        // Atributos de estilo padrão; ficam também nas properties da feature, de
        // onde o tema data-driven do draw (drawStyles) os lê via `user_*`.
        const styleAttrs = { fillColor: color, fillOpacity: 0.5, strokeColor: '#000000', strokeWeight: 3 };
        Object.entries(styleAttrs).forEach(([k, v]) => this.draw.setFeatureProperty(newShape.id, k, v));

        // Registra a forma na lista (independente de conter empreendimentos).
        this.setState((prev) => ({
            drawnShapes: [...prev.drawnShapes, {
                drawId: newShape.id,
                areaId,
                type,
                name,
                color,
                visible: true,
                feature: this.draw.get(newShape.id) || newShape,
                center: newShape.properties?.center || null,
                radiusMeters: newShape.properties?.radiusMeters || null,
                ...styleAttrs,
            }],
        }));

        // A análise da área só faz sentido com empreendimentos carregados e dentro da forma.
        if (this.allFeatures.length === 0) return;

        const buildingsInShape = this.allFeatures.filter((point) => {
            try {
                return booleanPointInPolygon(point, newShape);
            } catch {
                return false;
            }
        });

        if (buildingsInShape.length === 0) {
            toast.info('Nenhum empreendimento encontrado na área desenhada.');
            return;
        }

        const buildingIds = buildingsInShape.map((f) => f.properties.id);
        this.fetchAnalysisByBuildings(buildingIds, name, areaId, color);
    };

    handleDrawUpdate = (e) => {
        (e.features || []).forEach((feat) => {
            this.setState((prev) => ({
                drawnShapes: prev.drawnShapes.map((s) => s.drawId === feat.id
                    ? {
                        ...s,
                        feature: feat,
                        center: feat.properties?.center || s.center,
                        radiusMeters: feat.properties?.radiusMeters || s.radiusMeters,
                    }
                    : s),
            }));
        });
        this.applyShapeFilter();
    };

    // Ids removidos do draw intencionalmente por "esconder" — a forma deve
    // permanecer na lista (apenas oculta), então handleDrawDelete os ignora.
    _hidingIds = new Set();

    handleDrawDelete = (e) => {
        const deletedIds = (e.features || [])
            .map((f) => f.id)
            .filter((id) => {
                if (this._hidingIds.has(id)) { this._hidingIds.delete(id); return false; }
                return true;
            });
        if (deletedIds.length === 0) { this.applyShapeFilter(); return; }

        this.setState((prev) => {
            const removedAreaIds = prev.drawnShapes
                .filter((s) => deletedIds.includes(s.drawId))
                .map((s) => s.areaId);
            const searchAreas = prev.searchAreas.filter((a) => !removedAreaIds.includes(a.id));
            return {
                drawnShapes: prev.drawnShapes.filter((s) => !deletedIds.includes(s.drawId)),
                searchAreas,
                right_panel_visible: searchAreas.length > 0 && prev.right_panel_visible,
            };
        });
        this.applyShapeFilter();
    };

    // ===== KML: exportação e importação =====

    // Exporta as formas desenhadas (polígonos + círculos) e os empreendimentos
    // exibidos como KML, reusando o backend do mapa Google (KmlService.download →
    // route('download-kml-mapa')). Círculos do draw são turf polygons com
    // properties.center/radiusMeters; convertidos de volta para {lat,lng,raio}.
    handleDownloadKml = async () => {
        const features = this.draw?.getAll().features || [];
        const polygons = [];
        const circles = [];

        features.forEach((f) => {
            if (f.geometry?.type !== 'Polygon') return;
            const isCircle = ['circle', 'marker_circle'].includes(f.properties?.shapeType);
            if (isCircle && f.properties?.center && f.properties?.radiusMeters) {
                circles.push({
                    latitude: f.properties.center[1],
                    longitude: f.properties.center[0],
                    radius: f.properties.radiusMeters,
                });
            } else {
                // Anel externo GeoJSON [lng,lat] → [lat,lng] esperado pelo KmlCreator.
                const ring = f.geometry.coordinates?.[0] || [];
                polygons.push(ring.map(([lng, lat]) => [lat, lng]));
            }
        });

        // Empreendimentos exibidos, mapeados para o formato esperado pelo backend.
        const sourceFeatures = this.currentGeojson?.features || this.allFeatures || [];
        const empreendimentos = sourceFeatures.map((f) => {
            const p = f.properties || {};
            return {
                _id: p.id,
                nome: p.name || '',
                tipo_empreendimento: p.type || '',
                endereco: p.address || '',
                bairro: p.neighborhood || '',
                marker: p.icon || '',
                location: { coordinates: f.geometry?.coordinates || null },
            };
        });

        if (polygons.length === 0 && circles.length === 0 && empreendimentos.length === 0) {
            toast.info('Nada para exportar. Desenhe uma forma ou faça uma busca primeiro.');
            return;
        }

        try {
            this.setState({ loadingKml: true });
            await KmlService.download({ polygons, circles, empreendimentos });
        } catch (e) {
            console.error('[MapboxMap] erro ao exportar KML:', e);
            toast.error('Erro ao exportar o KML.');
        } finally {
            this.setState({ loadingKml: false });
        }
    };

    // Importa KML/KMZ: envia ao backend (route('transform-kml')), que devolve os
    // polígonos como arrays de [lat,lng]; cada um é adicionado ao draw.
    handleImportKmlFile = async (file) => {
        if (!file) return;
        const formData = new FormData();
        formData.append('file', file);

        try {
            this.setState({ loadingKml: true });
            const response = await axios.post(route('transform-kml'), formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            if (response.status !== 200 || !Array.isArray(response.data) || response.data.length === 0) {
                toast.warn('Nenhum polígono encontrado no arquivo.');
                return;
            }

            await this.addImportedPolygons(response.data);
            toast.success(`${response.data.length} forma(s) importada(s).`);
        } catch (e) {
            console.error('[MapboxMap] erro ao importar KML/KMZ:', e);
            toast.error('Erro ao importar o arquivo KML/KMZ.');
        } finally {
            this.setState({ loadingKml: false });
        }
    };

    // Adiciona ao draw os polígonos importados (cada path = array de [lat,lng]),
    // registra na lista de formas, centraliza o mapa e dispara o carregamento dos
    // empreendimentos dentro das formas (via cidades resolvidas por geocodificação reversa).
    addImportedPolygons = async (paths) => {
        if (!this.draw) return;

        const newShapes = [];
        const centers = [];
        const allBounds = new mapboxgl.LngLatBounds();

        paths.forEach((path, i) => {
            if (!Array.isArray(path) || path.length < 3) return;

            const ring = path.map(([lat, lng]) => [lng, lat]);
            const first = ring[0];
            const last = ring[ring.length - 1];
            if (first[0] !== last[0] || first[1] !== last[1]) ring.push(first);

            const feature = { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [ring] } };
            const [drawId] = this.draw.add(feature);

            const idx = this.state.drawnShapes.length + newShapes.length;
            const type = 'Polígono';
            const name = `${type} ${idx + 1}`;
            const color = colors[idx % colors.length];
            const areaId = `drawn-${Date.now()}-${i}`;
            const styleAttrs = { fillColor: color, fillOpacity: 0.5, strokeColor: '#000000', strokeWeight: 3 };
            Object.entries(styleAttrs).forEach(([k, v]) => this.draw.setFeatureProperty(drawId, k, v));

            // Centróide (centro do bounding box) p/ geocodificação reversa → cidade.
            const b = new mapboxgl.LngLatBounds();
            ring.forEach((c) => { b.extend(c); allBounds.extend(c); });
            const ctr = b.getCenter();
            centers.push({ lat: ctr.lat, lng: ctr.lng });

            newShapes.push({
                drawId, areaId, type, name, color, visible: true,
                feature: this.draw.get(drawId), center: null, radiusMeters: null, ...styleAttrs,
            });
        });

        if (newShapes.length === 0) {
            toast.warn('Nenhuma forma válida no arquivo.');
            return;
        }

        await new Promise((resolve) =>
            this.setState((prev) => ({ drawnShapes: [...prev.drawnShapes, ...newShapes] }), resolve)
        );

        if (!allBounds.isEmpty()) {
            this.map.fitBounds(allBounds, { padding: 60, duration: 600 });
        }

        // Carrega do backend os empreendimentos dentro das formas importadas.
        await this.loadEmpreendimentosForImportedShapes(centers, newShapes);
    };

    // Resolve as cidades das formas importadas (geocodificação reversa dos centróides),
    // sincroniza o filtro de cidade, busca os empreendimentos dessas cidades e recorta
    // pelas formas (applyShapeFilter, dentro de fetchAndRender). Em seguida roda a análise
    // por forma para popular o painel de resultados.
    loadEmpreendimentosForImportedShapes = async (centers, importedShapes) => {
        const resolved = [];
        for (const c of centers) {
            try {
                const r = await geolocationService.reverse(c.lat, c.lng, null);
                if (r?.uf && r?.cidade) {
                    resolved.push({ value: `${r.uf}-${r.cidade}`, label: `${r.cidade} - ${r.uf}` });
                }
            } catch (e) {
                console.error('[MapboxMap] falha na geocodificação reversa de forma importada:', e);
            }
        }

        // União com as cidades já selecionadas (preserva a busca atual).
        const current = this.formFiltersRef.current?.getFilters?.()?.cidades || [];
        const byValue = new Map();
        [...current, ...resolved].forEach((c) => byValue.set(c.value, c));
        const cidades = [...byValue.values()];

        if (cidades.length === 0) {
            toast.info('Não foi possível identificar a cidade das formas importadas. Selecione uma cidade e busque.');
            return;
        }

        // Sincroniza a cidade no formulário (prop observada por FormFiltersContainer).
        this.setState({ cidadeSincronizada: cidades });

        const currentFilters = this.formFiltersRef.current?.getFilters?.() || {};
        const payload = this.buildFiltersPayload({ ...currentFilters, cidades });

        // Carrega allFeatures e recorta pelas formas (incl. as importadas).
        await this.fetchAndRender(payload);

        // Análise por forma importada (popula o painel de resultados).
        if (this.allFeatures.length > 0) {
            importedShapes.forEach(({ drawId, areaId, name, color }) => {
                const shapeFeature = this.draw.get(drawId);
                if (!shapeFeature) return;
                const inside = this.allFeatures.filter((pt) => {
                    try { return booleanPointInPolygon(pt, shapeFeature); } catch { return false; }
                });
                if (inside.length > 0) {
                    this.fetchAnalysisByBuildings(inside.map((f) => f.properties.id), name, areaId, color);
                }
            });
        }

        this.refreshBairrosAvailability(cidades);
        this.refreshZoneamentoAvailability(cidades);
        this.loadYearsOptionsForLinkagesDataLayer(cidades);
    };

    // ===== Detalhe do empreendimento (modal ao clicar no marcador) =====

    // Busca o detalhe completo do empreendimento (fotos, unidades, tabelas, etc.)
    // pelo id do Building e abre o ModalEmpreendimentos (reuso do mapa Google).
    openEmpreendimentoModal = async (buildingId) => {
        if (!buildingId || this.state.loadingEmpreendimentoDetail) return;

        this.setState({ loadingEmpreendimentoDetail: true });
        try {
            const res = await axios.get(`/api/empreendimentos-mapbox/${buildingId}/detalhe`);
            this.setState({ empreendimentoDetail: res.data, showEmpreendimentoModal: true });
        } catch (e) {
            console.error('[MapboxMap] erro ao carregar detalhe do empreendimento:', e);
            toast.error('Não foi possível carregar os detalhes do empreendimento.');
        } finally {
            this.setState({ loadingEmpreendimentoDetail: false });
        }
    };

    closeEmpreendimentoModal = () => this.setState({ showEmpreendimentoModal: false, empreendimentoDetail: null });

    // ===== Ações da lista de formas desenhadas =====
    updateShape = (drawId, patch) => this.setState((prev) => ({
        drawnShapes: prev.drawnShapes.map((s) => s.drawId === drawId ? { ...s, ...patch } : s),
    }));

    centerOnShape = (shape) => {
        const feat = shape.visible ? this.draw?.get(shape.drawId) : shape.feature;
        if (!feat?.geometry?.coordinates) return;

        const bounds = new mapboxgl.LngLatBounds();
        const walk = (coords) => Array.isArray(coords[0]) ? coords.forEach(walk) : bounds.extend(coords);
        walk(feat.geometry.coordinates);
        this.map.fitBounds(bounds, { padding: 60, duration: 600 });
    };

    renameShape = (shape, novoNome) => {
        const name = (novoNome || '').trim() || shape.name;
        this.setState((prev) => ({
            drawnShapes: prev.drawnShapes.map((s) => s.drawId === shape.drawId ? { ...s, name } : s),
            searchAreas: prev.searchAreas.map((a) => a.id === shape.areaId ? { ...a, name } : a),
        }));
    };

    toggleShapeVisibility = (shape) => {
        if (shape.visible) {
            const feat = this.draw?.get(shape.drawId) || shape.feature;
            this._hidingIds.add(shape.drawId); // não remover da lista no draw.delete resultante
            this.draw?.delete(shape.drawId);
            this.updateShape(shape.drawId, { visible: false, feature: feat });
        } else {
            const [id] = this.draw?.add(shape.feature) || [];
            this.updateShape(shape.drawId, { visible: true, drawId: id ?? shape.drawId });
        }
        this.applyShapeFilter();
    };

    setShapeRadius = (shape, radiusMeters) => {
        if (!shape.center || !(radiusMeters > 0)) return;
        const shapeType = shape.feature?.properties?.shapeType || 'circle';
        const updated = makeCircleFeature(shape.center, radiusMeters, shapeType);
        // Preserva os atributos de estilo (cor/opacidade/borda) ao regenerar a geometria.
        updated.properties = {
            ...updated.properties,
            fillColor: shape.fillColor,
            fillOpacity: shape.fillOpacity,
            strokeColor: shape.strokeColor,
            strokeWeight: shape.strokeWeight,
        };
        // Mantém o mesmo id → MapboxDraw atualiza a feature no lugar (sem delete+add,
        // logo sem disparar draw.delete nem race de renderização).
        updated.id = shape.drawId;

        if (shape.visible) this.draw?.add(updated);
        this.updateShape(shape.drawId, { radiusMeters, feature: updated });
        this.applyShapeFilter();
    };

    removeShape = (shape) => {
        // draw.delete() programático não dispara o evento draw.delete, então a
        // limpeza da lista/área é feita aqui mesmo (a lixeira nativa usa handleDrawDelete).
        if (shape.visible) this.draw?.delete(shape.drawId);
        this._hidingIds.delete(shape.drawId);
        this.setState((prev) => {
            const searchAreas = prev.searchAreas.filter((a) => a.id !== shape.areaId);
            return {
                drawnShapes: prev.drawnShapes.filter((s) => s.drawId !== shape.drawId),
                searchAreas,
                right_panel_visible: searchAreas.length > 0 && prev.right_panel_visible,
            };
        });
        this.applyShapeFilter();
    };

    editShapeAttributes = (shape) => this.setState({ shapeEditing: shape, shapeEditorOpen: true });

    closeShapeEditor = () => this.setState({ shapeEditorOpen: false, shapeEditing: null });

    // Aplica os atributos editados (cor/opacidade/borda/raio/rótulo) à forma.
    // Reusa o padrão "re-add com mesmo id" (igual setShapeRadius): MapboxDraw
    // atualiza a feature no lugar, sem delete+add.
    applyShapeAttributes = (shape, attrs) => {
        if (!shape) return;

        const base = (shape.visible && this.draw?.get(shape.drawId)) || shape.feature;
        const updated = {
            ...base,
            id: shape.drawId,
            properties: {
                ...base.properties,
                fillColor: attrs.fillColor,
                fillOpacity: attrs.fillOpacity,
                strokeColor: attrs.strokeColor,
                strokeWeight: attrs.strokeWeight,
            },
        };

        // Raio (somente círculo): regenera a geometria mantendo o centro.
        if (shape.type === 'Círculo' && attrs.radiusMeters > 0 && shape.center) {
            const st = updated.properties?.shapeType || 'circle';
            const geo = makeCircleFeature(shape.center, attrs.radiusMeters, st);
            updated.geometry = geo.geometry;
            updated.properties = { ...updated.properties, radiusMeters: attrs.radiusMeters };
        }

        if (shape.visible) {
            this.draw?.add(updated);
            // Sai do estado "ativo" (laranja de edição) para que a cor/opacidade
            // customizada da forma apareça imediatamente (drawStyles só aplica
            // user_* quando active=false).
            this.draw?.changeMode('simple_select', { featureIds: [] });
        }

        const name = (attrs.name || '').trim() || shape.name;
        const cor = attrs.fillColor === 'transparent' ? attrs.strokeColor : attrs.fillColor;

        this.updateShape(shape.drawId, {
            name,
            color: cor,
            fillColor: attrs.fillColor,
            fillOpacity: attrs.fillOpacity,
            strokeColor: attrs.strokeColor,
            strokeWeight: attrs.strokeWeight,
            radiusMeters: shape.type === 'Círculo' && attrs.radiusMeters > 0 ? attrs.radiusMeters : shape.radiusMeters,
            feature: updated,
        });

        // Sincroniza nome/cor no painel de resultados.
        this.setState((prev) => ({
            searchAreas: prev.searchAreas.map((a) => a.id === shape.areaId ? { ...a, name, color: cor } : a),
        }));

        this.applyShapeFilter();
        this.closeShapeEditor();
    };

    // ===== Raio por Deslocamento (isócrona) =====

    // Marcador do endereço buscado (mapboxgl.Marker é overlay DOM: sobrevive à troca
    // de estilo). Clicar nele abre o modal de Raio por Deslocamento com o endereço
    // como ponto de partida.
    addressMarker = null;

    setAddressMarker = (lat, lng, label) => {
        if (!this.map) return;
        this.addressMarker?.remove();

        this.addressMarker = new mapboxgl.Marker({ color: '#5B7537' })
            .setLngLat([lng, lat])
            .addTo(this.map);

        const el = this.addressMarker.getElement();
        el.style.cursor = 'pointer';
        el.title = `${label ? `${label} — ` : ''}Clique para traçar o Raio por Deslocamento`;
        el.addEventListener('click', (e) => {
            e.stopPropagation();
            this.setState({
                isochroneCenter: { lat, lng },
                showRaioDeslocamento: true,
            });
        });
    };

    removeAddressMarker = () => {
        this.addressMarker?.remove();
        this.addressMarker = null;
    };

    // Ativa o modo de escolha do ponto de partida: o próximo clique no mapa define o
    // centro e abre o modal. Clicar de novo no botão cancela a escolha.
    toggleIsochronePick = () => {
        if (!this.map) return;

        if (this.state.pickingIsochroneCenter) {
            this.map.off('click', this.handleIsochroneCenterPick);
            this.map.getCanvas().style.cursor = '';
            this.setState({ pickingIsochroneCenter: false });
            return;
        }

        this.setState({ pickingIsochroneCenter: true });
        this.map.getCanvas().style.cursor = 'crosshair';
        this.map.once('click', this.handleIsochroneCenterPick);
        toast.info('Clique no mapa para definir o ponto de partida do deslocamento.');
    };

    handleIsochroneCenterPick = (e) => {
        this.map.getCanvas().style.cursor = '';
        this.setState({
            pickingIsochroneCenter: false,
            isochroneCenter: { lat: e.lngLat.lat, lng: e.lngLat.lng },
            showRaioDeslocamento: true,
        });
    };

    // Adiciona as isócronas ao draw como formas (recortam empreendimentos como qualquer
    // polígono desenhado), registra na lista de formas e roda a análise por área de cada uma.
    handleIsochronesReady = async (isochrones, tipoLabel) => {
        if (!this.draw) return;

        const newShapes = [];
        const allBounds = new mapboxgl.LngLatBounds();
        const extendBounds = (coords) => {
            if (typeof coords[0] === 'number') { allBounds.extend(coords); return; }
            coords.forEach(extendBounds);
        };

        isochrones.forEach(({ feature, tempo, cor }, i) => {
            const polygon = {
                type: 'Feature',
                properties: { shapeType: 'isochrone' },
                geometry: feature.geometry,
            };
            const [drawId] = this.draw.add(polygon);

            const name = `Deslocamento ${tipoLabel} em ${tempo} minutos`;
            const areaId = `drawn-${Date.now()}-${i}`;
            // Paridade com o mapa Google: isócrona sem preenchimento, só o contorno colorido.
            const styleAttrs = { fillColor: cor, fillOpacity: 0, strokeColor: cor, strokeWeight: 4 };
            Object.entries(styleAttrs).forEach(([k, v]) => this.draw.setFeatureProperty(drawId, k, v));

            extendBounds(feature.geometry.coordinates || []);

            newShapes.push({
                drawId, areaId, type: 'Isócrona', name, color: cor, visible: true,
                feature: this.draw.get(drawId), center: null, radiusMeters: null, ...styleAttrs,
            });
        });

        await new Promise((resolve) =>
            this.setState((prev) => ({ drawnShapes: [...prev.drawnShapes, ...newShapes] }), resolve)
        );

        if (!allBounds.isEmpty()) {
            this.map.fitBounds(allBounds, { padding: 60, duration: 600 });
        }

        this.applyShapeFilter();
        this.runShapeAnalyses(newShapes);
    };

    // Análise por área (como no handleDrawCreate) para formas adicionadas
    // programaticamente (isócronas, projeto carregado), se há empreendimentos carregados.
    runShapeAnalyses = (shapes) => {
        if (this.allFeatures.length === 0) return;
        shapes.forEach((shape) => {
            const buildingIds = this.allFeatures
                .filter((point) => {
                    try {
                        return booleanPointInPolygon(point, shape.feature);
                    } catch {
                        return false;
                    }
                })
                .map((f) => f.properties.id);

            if (buildingIds.length > 0) {
                this.fetchAnalysisByBuildings(buildingIds, shape.name, shape.areaId, shape.color);
            }
        });
    };

    fetchAnalysisByBuildings = async (buildingIds, areaName, areaId, color) => {
        this.setState((prev) => ({
            searchAreas: [...prev.searchAreas, { id: areaId, name: areaName, color, loading: true, meta_data: null, isDrawnArea: true }],
            right_panel_visible: true,
        }));

        try {
            const res = await axios.post('/api/empreendimentos-mapbox/analysis-by-area', { building_ids: buildingIds });
            const { meta_data } = res.data;

            this.setState((prev) => ({
                searchAreas: prev.searchAreas.map((a) =>
                    a.id === areaId ? { ...a, meta_data, loading: false } : a
                ),
            }));
        } catch (e) {
            console.error('[MapboxMap] erro análise área desenhada:', e);
            toast.error('Erro ao carregar dados de análise da área.');
            this.setState((prev) => ({
                searchAreas: prev.searchAreas.filter((a) => a.id !== areaId),
            }));
        }
    };

    // ===== Projetos (salvar / carregar / excluir / compartilhar) =====
    // Reusa o backend do mapa Google (MapaState/StoreMapaProjectService), serializando
    // as formas no formato legado para que projetos sejam intercambiáveis entre os mapas.

    // Formas do draw → formato legado: polígonos como caminhos [[lat,lng],...] (sem o
    // ponto de fechamento) e círculos como {latitude, longitude, radius}.
    serializeShapesForProject = () => {
        const polygons = [];
        const circles = [];

        this.state.drawnShapes.forEach((shape) => {
            const feature = this.draw?.get(shape.drawId) || shape.feature;
            if (!feature) return;

            const style = {
                color: shape.fillColor,
                fillColor: shape.fillColor,
                strokeColor: shape.strokeColor,
                fillOpacity: shape.fillOpacity,
                strokeWeight: shape.strokeWeight,
            };

            if (['circle', 'marker_circle'].includes(feature.properties?.shapeType)) {
                const center = feature.properties.center || shape.center;
                const radius = feature.properties.radiusMeters || shape.radiusMeters;
                if (!center || !radius) return;
                circles.push({
                    latitude: center[1],
                    longitude: center[0],
                    radius,
                    name: shape.name,
                    type: shape.type,
                    ...style,
                });
                return;
            }

            if (feature.geometry?.type !== 'Polygon') return;
            const ring = feature.geometry.coordinates[0] || [];
            const path = ring.slice(0, -1).map(([lng, lat]) => [lat, lng]);
            if (path.length < 3) return;
            polygons.push({ name: shape.name, polygon: path, ...style });
        });

        return { polygons, circles };
    };

    // Payload no shape que o StoreMapaProjectService espera (o service acessa a maioria
    // das chaves incondicionalmente — todas precisam existir, mesmo vazias).
    createProjectPayload = () => {
        const filters = this.formFiltersRef.current?.getFilters?.() || {};
        const { polygons, circles } = this.serializeShapesForProject();
        const center = this.map?.getCenter();

        return {
            project_name: this.state.projectName,
            share_users: this.state.projectShareUsers.map((u) => u.id),
            empreendimentos: [],
            address: null,
            cidade: filters.cidades || [],
            estado: filters.estados || [],
            tipo_empreendimento: filters.tipo || [],
            padrao: filters.padrao || [],
            status: filters.status || [],
            incorporadoras: filters.incorporadoras || [],
            bairros: filters.bairros || [],
            quartos: filters.quartos || [],
            ano_lancamento: filters.ano_lancamento || [],
            estagio: filters.estagio || [],
            polygons,
            circles,
            area_maior_que: filters.area_maior_que || null,
            area_menor_que: filters.area_menor_que || null,
            valor_maior_que: filters.valor_maior_que || null,
            valor_menor_que: filters.valor_menor_que || null,
            valor_metro_maior_que: filters.valor_metro_maior_que || null,
            valor_metro_menor_que: filters.valor_metro_menor_que || null,
            juros_minimo: filters.juros_minimo || null,
            juros_maximo: filters.juros_maximo || null,
            valor_parcela_minima: filters.valor_parcela_minima || null,
            valor_parcela_maxima: filters.valor_parcela_maxima || null,
            parcelas_minimo: filters.parcelas_minimo || null,
            parcelas_maximo: filters.parcelas_maximo || null,
            porcentagem_vendida_minima: filters.porcentagem_vendida_minima || null,
            porcentagem_vendida_maxima: filters.porcentagem_vendida_maxima || null,
            mes_ano_lancamento: filters.mes_ano_lancamento || null,
            currentPosition: { lat: center?.lat ?? null, lng: center?.lng ?? null },
            raio: 3000,
            ranges: this.state.ranges,
            color_ranges: Object.keys(this.state.color_ranges || {}).length ? this.state.color_ranges : null,
            datalayerName: this.state.datalayerName || null,
            datalayerColorProperty: this.state.datalayerColorProperty || null,
            datalayerYear: this.state.datalayerYear || null,
            datalayerYears: this.state.datalayerYears?.length ? this.state.datalayerYears : null,
            datalayer: null,
        };
    };

    saveProject = async () => {
        this.setState({ savingProject: true, projectErrors: {} });
        try {
            const response = await axios.post('/mapa-state/projeto', this.createProjectPayload());
            toast.success(response.data.message);
            this.setState({ modalSalvarProjetoOpen: false, projectName: '', projectShareUsers: [] });
            this.formFiltersRef.current?.getProjects?.();
        } catch (error) {
            console.error('[MapboxMap] erro ao salvar projeto:', error);
            if (error.response?.data?.errors) {
                this.setState({ projectErrors: error.response.data.errors });
            } else {
                toast.error('Erro ao salvar o projeto.');
            }
        } finally {
            this.setState({ savingProject: false });
        }
    };

    // Campos do projeto chegam como string JSON ou já decodificados (o show() do
    // MapaState converte alguns para arrays de valores) — normaliza os dois casos.
    parseProjectField = (value, fallback = []) => {
        if (value == null) return fallback;
        if (typeof value === 'string') {
            try {
                return JSON.parse(value) ?? fallback;
            } catch {
                return fallback;
            }
        }
        return value;
    };

    handleProjetoSelected = async (projectId) => {
        try {
            const response = await axios.get(route('mapa.project.show', projectId));
            const p = response.data.project;

            // Limpa o mapa (formas, camadas, áreas). Os filtros do form são
            // sobrescritos integralmente por applyProjectFilters logo abaixo.
            this.handleFiltersClear();

            await this.formFiltersRef.current?.applyProjectFilters?.({
                cidade: this.parseProjectField(p.cidade),
                estadosSelecionados: this.parseProjectField(p.estado),
                status: this.parseProjectField(p.status, ['Ativo']),
                padrao: this.parseProjectField(p.padrao),
                tipo_empreendimento: this.parseProjectField(p.tipo_empreendimento),
                bairros: this.parseProjectField(p.bairros),
                quartos: this.parseProjectField(p.quartos).map(Number),
                ano_lancamento: this.parseProjectField(p.ano_lancamento).map(Number),
                estagio: this.parseProjectField(p.estagio),
                incorporadoras: this.parseProjectField(p.incorporadoras),
                area_maior_que: p.area_maior_que ?? '',
                area_menor_que: p.area_menor_que ?? '',
                valor_maior_que: p.valor_maior_que ?? '',
                valor_menor_que: p.valor_menor_que ?? '',
                valor_metro_maior_que: p.valor_metro_maior_que ?? '',
                valor_metro_menor_que: p.valor_metro_menor_que ?? '',
                juros_minimo: p.juros_minimo ?? '',
                juros_maximo: p.juros_maximo ?? '',
                parcelas_minimo: p.parcelas_minimo ?? '',
                parcelas_maximo: p.parcelas_maximo ?? '',
                valor_parcela_minima: p.valor_parcela_minima ?? '',
                valor_parcela_maxima: p.valor_parcela_maxima ?? '',
                porcentagem_vendida_minima: p.porcentagem_vendida_minima ?? '',
                porcentagem_vendida_maxima: p.porcentagem_vendida_maxima ?? '',
                mes_ano_lancamento: p.mes_ano_lancamento ? new Date(p.mes_ano_lancamento) : '',
            });

            // Ranges de marcadores + faixas de cor dos dados censitários
            // (JSON.stringify serializa Infinity como null; restaura ao carregar).
            const ranges = this.parseProjectField(p.ranges);
            const colorRanges = this.parseProjectField(p.color_ranges, null);
            if (colorRanges) {
                Object.keys(colorRanges).forEach((key) => {
                    if (Array.isArray(colorRanges[key])) {
                        colorRanges[key] = colorRanges[key].map((r) => ({
                            ...r,
                            min: r.min === null ? -Infinity : r.min,
                            max: r.max === null ? Infinity : r.max,
                        }));
                    }
                });
            }
            await new Promise((resolve) =>
                this.setState(
                    { ranges, color_ranges: colorRanges || {}, loadedProjectId: projectId },
                    resolve
                )
            );

            // Recria as formas salvas e dispara a busca com os filtros do projeto.
            const newShapes = this.addProjectShapes(
                this.parseProjectField(p.polignos),
                this.parseProjectField(p.manualCircles)
            );

            const filters = this.formFiltersRef.current?.getFilters?.() || {};
            if ((filters.cidades || []).length > 0 || (filters.estados || []).length > 0) {
                await this.handleFiltersSubmit(filters);
                this.runShapeAnalyses(newShapes);
            }

            // Restaura a camada de dados censitários, se o projeto tinha uma ativa.
            const datalayerName = this.parseProjectField(p.datalayerName, '');
            if (datalayerName) {
                await new Promise((resolve) =>
                    this.setState(
                        {
                            datalayerName,
                            datalayerFriendlyName: LinkagesDataLayerService.getLayerLabel(datalayerName),
                            datalayerColorPropertyOptions: LinkagesDataLayerService.getOptionsForColorMap(datalayerName),
                            datalayerColorProperty: this.parseProjectField(p.datalayerColorProperty, null),
                            datalayerYear: this.parseProjectField(p.datalayerYear, null),
                            datalayerYears: this.parseProjectField(p.datalayerYears),
                        },
                        resolve
                    )
                );
                this.loadDataLayer();
            }

            toast.success('Projeto carregado.');
        } catch (e) {
            console.error('[MapboxMap] erro ao carregar projeto:', e);
            toast.error('Erro ao carregar o projeto.');
        }
    };

    // Recria no draw as formas salvas no projeto (formato legado do mapa Google).
    addProjectShapes = (polygons, circles) => {
        if (!this.draw) return [];

        const newShapes = [];
        const allBounds = new mapboxgl.LngLatBounds();

        const register = (drawId, name, type, style) => {
            const idx = this.state.drawnShapes.length + newShapes.length;
            const color = (style.color || style.fillColor) || colors[idx % colors.length];
            const areaId = `drawn-${Date.now()}-${idx}`;
            const styleAttrs = {
                fillColor: color,
                fillOpacity: style.fillOpacity ?? 0.5,
                strokeColor: style.strokeColor || '#000000',
                strokeWeight: style.strokeWeight ?? 3,
            };
            Object.entries(styleAttrs).forEach(([k, v]) => this.draw.setFeatureProperty(drawId, k, v));

            const feature = this.draw.get(drawId);
            newShapes.push({
                drawId, areaId, type, name: name || `${type} ${idx + 1}`, color, visible: true,
                feature,
                center: feature?.properties?.center || null,
                radiusMeters: feature?.properties?.radiusMeters || null,
                ...styleAttrs,
            });
        };

        (polygons || []).forEach((data) => {
            // Formato atual: {name, polygon: [[lat,lng],...], ...estilos}; projetos
            // antigos podem ter só o caminho.
            const path = data?.polygon ?? data;
            if (!Array.isArray(path) || path.length < 3) return;

            const ring = path.map(([lat, lng]) => [lng, lat]);
            const first = ring[0];
            const last = ring[ring.length - 1];
            if (first[0] !== last[0] || first[1] !== last[1]) ring.push([...first]);

            const [drawId] = this.draw.add({
                type: 'Feature',
                properties: {},
                geometry: { type: 'Polygon', coordinates: [ring] },
            });
            register(drawId, data?.name, 'Polígono', data || {});
            ring.forEach((c) => allBounds.extend(c));
        });

        (circles || []).forEach((data) => {
            if (!data) return;
            const lat = parseFloat(data.center?.lat ?? data.latitude);
            const lng = parseFloat(data.center?.lng ?? data.longitude);
            if (!Number.isFinite(lat) || !Number.isFinite(lng) || !data.radius) return;

            const feature = makeCircleFeature([lng, lat], data.radius, 'circle');
            const [drawId] = this.draw.add(feature);
            register(drawId, data.name, data.type || 'Círculo', data);
            (feature.geometry.coordinates[0] || []).forEach((c) => allBounds.extend(c));
        });

        if (newShapes.length > 0) {
            this.setState((prev) => ({ drawnShapes: [...prev.drawnShapes, ...newShapes] }));
            if (!allBounds.isEmpty()) {
                this.map.fitBounds(allBounds, { padding: 60, duration: 600 });
            }
        }

        return newShapes;
    };

    deleteProject = async () => {
        const projectId = this.state.loadedProjectId;
        if (!projectId) return;
        if (!window.confirm('Excluir o projeto carregado? Esta ação não pode ser desfeita.')) return;

        try {
            const response = await axios.delete(route('mapa.project.delete', projectId));
            toast.success(response.data.message);
            this.formFiltersRef.current?.cleanState?.();
            this.formFiltersRef.current?.getProjects?.();
        } catch (e) {
            console.error('[MapboxMap] erro ao excluir projeto:', e);
            toast.error('Erro ao excluir o projeto.');
        }
    };

    // ===== Ranges customizados de marcadores (ModalRanges) =====

    addRange = (range) => this.setState((prev) => ({ ranges: [...prev.ranges, range] }));

    updateRange = (range) =>
        this.setState((prev) => ({
            ranges: prev.ranges.map((r) => (r.id === range.id ? { ...r, ...range } : r)),
        }));

    removeRange = (range) =>
        this.setState((prev) => ({ ranges: prev.ranges.filter((r) => r.id !== range.id) }));

    // "Aplicar" do ModalRanges: registra as imagens dos marcadores no mapa, marca cada
    // feature com o range correspondente e re-renderiza a source.
    applyMarkerRanges = async () => {
        if (!this.map) return;
        await this.ensureRangeImages();
        this.tagFeaturesWithRanges();
        this.applyShapeFilter();
    };

    // Desenha um pin estilo Google PinElement (cabeça + haste) com as cores/escala do
    // range. Base ~40x53px em escala 1 (icon-size 0.6 na camada ≈ tamanho dos PNGs).
    createRangePinImage = (marker) => {
        const scale = marker.scale || 1;
        const w = Math.max(8, Math.round(40 * scale));
        const h = Math.max(10, Math.round(53 * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');

        const cx = w / 2;
        const r = w * 0.42;
        const cy = r + 2 * scale;

        // Gota: arco da cabeça (de baixo-esquerda a baixo-direita, passando pelo topo)
        // + ponta inferior (âncora bottom da camada).
        ctx.beginPath();
        ctx.arc(cx, cy, r, Math.PI * 0.75, Math.PI * 0.25);
        ctx.lineTo(cx, h - 1);
        ctx.closePath();
        ctx.fillStyle = marker.background || '#cccccc';
        ctx.fill();
        ctx.lineWidth = Math.max(1, 2 * scale);
        ctx.strokeStyle = marker.borderColor || '#000000';
        ctx.stroke();

        // Glifo central.
        ctx.beginPath();
        ctx.arc(cx, cy, r * 0.35, 0, 2 * Math.PI);
        ctx.fillStyle = marker.glyphColor || '#000000';
        ctx.fill();

        return ctx.getImageData(0, 0, w, h);
    };

    rangeImageId = (range) => `range-marker-${range.id}`;

    // Garante que cada range tem sua imagem registrada no estilo atual (as imagens
    // são perdidas na troca de estilo). Cor → canvas; imagem → map.loadImage(url).
    ensureRangeImages = () =>
        Promise.all(
            this.state.ranges.map((range) => {
                const id = this.rangeImageId(range);
                if (this.map.hasImage(id)) return Promise.resolve();

                if (range.marker.type === 'image') {
                    return new Promise((resolve) => {
                        this.map.loadImage(range.marker.value, (err, image) => {
                            if (err || !image) {
                                toast.warn('Não foi possível carregar a imagem de um dos ranges.');
                                return resolve();
                            }
                            if (!this.map.hasImage(id)) this.map.addImage(id, image);
                            resolve();
                        });
                    });
                }

                this.map.addImage(id, this.createRangePinImage(range.marker));
                return Promise.resolve();
            })
        );

    // Marca cada feature com a imagem do primeiro range em que ela se encaixa
    // (mesma resolução do replaceMarkers do mapa Google). Sem match (ou sem ranges),
    // a camada cai no ícone padrão via coalesce.
    tagFeaturesWithRanges = () => {
        const { ranges } = this.state;
        this.allFeatures.forEach((f) => {
            delete f.properties.range_icon;
            if (ranges.length === 0) return;

            const range = ranges.find((r) => {
                if (r.type === 'padroes') return r.padroes.includes(f.properties.padrao);
                const value = Number(f.properties[RANGE_PROPERTY[r.type]] ?? NaN);
                return value >= r.min && value <= r.max;
            });

            if (range && this.map.hasImage(this.rangeImageId(range))) {
                f.properties.range_icon = this.rangeImageId(range);
            }
        });
    };

    initializeLayers = async () => {
        await this.loadMarkerImages();
        await this.loadHeatMarkerImages();
        await this.loadPolosImages();
        // Imagens dos ranges são perdidas na troca de estilo; re-registra antes do setData.
        if (this.state.ranges.length > 0) {
            await this.ensureRangeImages();
        }
        this.setupBairrosLayers();
        this.setupZoneamentoLayers();
        this.setupDatalayerLayers();
        this.setupClusterLayers();
        this.setupHeatLayers();
        this.setupPolosLayers();
        if (this.currentGeojson) {
            this.map.getSource(SOURCE_ID)?.setData(this.currentGeojson);
        }
        // Reaplica os limites de bairros se o toggle estiver ligado (ex.: após troca de estilo).
        if (this.state.showBairros) {
            const cidades = this.formFiltersRef.current?.getFilters?.()?.cidades || [];
            this.renderBairros(cidades);
        }
        // Reaplica o mapa de calor se estiver ativo (ex.: após troca de estilo, que
        // recria fontes/camadas), usando a última config escolhida.
        if (this.state.showHeatMap && this.heatMapConfig) {
            this.addHeatMap(this.heatMapConfig);
        }
        // Reaplica os polos geradores se estiverem ativos.
        if (this.state.showPolosGeradores && this.allPolos.length) {
            this.applyPolosShapeFilter();
            this.setPolosLayersVisibility(true);
        }
        // Reaplica a preferência de esconder empreendimentos (camadas recriadas visíveis).
        if (!this.state.showEmpreendimentos && !this.state.showHeatMap) {
            this.setEmpreendimentosVisibility(false);
        }
        // Reaplica o zoneamento se estiver ativo (ex.: após troca de estilo).
        if (this.state.showZoneamento && this.currentZoneamentoCidade) {
            this.showZoneamentoLayer();
        }
        // Reaplica os dados censitários se houver uma camada exibida (ex.: após troca de estilo).
        if (this.state.datalayerName && this.currentDatalayerGeojson) {
            this.map.getSource(DATALAYER_SOURCE_ID)?.setData(this.currentDatalayerGeojson);
        }
    };

    setupBairrosLayers = () => {
        if (this.map.getSource(BAIRROS_SOURCE_ID)) return;

        this.map.addSource(BAIRROS_SOURCE_ID, { type: 'geojson', data: EMPTY_FC });

        this.map.addLayer({
            id: 'bairros-fill',
            type: 'fill',
            source: BAIRROS_SOURCE_ID,
            paint: { 'fill-color': '#0d6efd', 'fill-opacity': 0.08 },
        });

        this.map.addLayer({
            id: 'bairros-outline',
            type: 'line',
            source: BAIRROS_SOURCE_ID,
            paint: { 'line-color': '#0d6efd', 'line-width': 1.5 },
        });

        this.map.addLayer({
            id: 'bairros-label',
            type: 'symbol',
            source: BAIRROS_SOURCE_ID,
            layout: {
                'text-field': ['get', 'name'],
                'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                'text-size': 11,
            },
            paint: {
                'text-color': '#0a4dad',
                'text-halo-color': '#ffffff',
                'text-halo-width': 1.2,
            },
        });
    };

    // ===== Dados censitários (camadas Linkages/IBGE) =====

    // Source vazia + camadas de preenchimento/contorno. A cor de cada setor vem da
    // propriedade `color` (definida por initializeColors). Espelha setDatalayerStyle/
    // addDatalayerListener do mapa Google. `dutovias` é renderizada como linha grossa.
    setupDatalayerLayers = () => {
        if (this.map.getSource(DATALAYER_SOURCE_ID)) return;

        this.map.addSource(DATALAYER_SOURCE_ID, { type: 'geojson', data: EMPTY_FC });

        const demarcacao = this.state.show_demarcacao_areas;

        this.map.addLayer({
            id: 'datalayer-fill',
            type: 'fill',
            source: DATALAYER_SOURCE_ID,
            paint: {
                'fill-color': ['coalesce', ['get', 'color'], '#cccccc'],
                'fill-opacity': this.state.datalayerFillOpacity,
            },
        });

        this.map.addLayer({
            id: 'datalayer-outline',
            type: 'line',
            source: DATALAYER_SOURCE_ID,
            paint: {
                'line-color': demarcacao ? '#000000' : ['coalesce', ['get', 'color'], '#cccccc'],
                'line-width': 1,
                'line-opacity': demarcacao ? 0.5 : 0.1,
            },
        });

        const datalayerPopup = new mapboxgl.Popup({ closeButton: true, closeOnClick: true });

        this.map.on('click', 'datalayer-fill', (e) => {
            const f = e.features?.[0];
            if (!f) return;
            const p = f.properties || {};

            let body = '';
            Object.entries(p).forEach(([label, value]) => {
                if (DATALAYER_IGNORE_PROPERTIES.includes(label)) return;
                if (value === null || value === undefined || value === '') return;

                const friendlyLabel = getLabel(label);
                const formattedValue = formater(label, value);
                body += `
                    <div class="info-window-item">
                        <span class="info-window-item-label font-weight-bold">${friendlyLabel}:</span>
                        <span class="info-window-item-value">${formattedValue}</span>
                    </div>`;
            });

            const title = LinkagesDataLayerService.getLayerLabel(this.state.datalayerName);
            const content = `
                <div class="info-window">
                    <div class="info-window-header">
                        <h5 class="info-window-title">${title}</h5>
                    </div>
                    <div class="info-window-body" style="max-height:400px;overflow-y:auto;">${body}</div>
                </div>`;

            datalayerPopup.setLngLat(e.lngLat).setHTML(content).addTo(this.map);
        });

        this.map.on('mouseenter', 'datalayer-fill', () => { this.map.getCanvas().style.cursor = 'pointer'; });
        this.map.on('mouseleave', 'datalayer-fill', () => { this.map.getCanvas().style.cursor = ''; });
    };

    // ===== Zoneamento =====

    // Source vazia + camadas de preenchimento/contorno. Espelha o estilo do mapa
    // Google: cor por sigla_zoneamento (`color`), opacidade 0.6, contorno azul. O
    // filtro de visibilidade por feature (`visible`) é alternado pelo FiltroZoneamento.
    setupZoneamentoLayers = () => {
        if (this.map.getSource(ZONEAMENTO_SOURCE_ID)) return;

        this.map.addSource(ZONEAMENTO_SOURCE_ID, { type: 'geojson', data: EMPTY_FC });

        this.map.addLayer({
            id: 'zoneamento-fill',
            type: 'fill',
            source: ZONEAMENTO_SOURCE_ID,
            filter: ZONEAMENTO_VISIBLE_FILTER,
            paint: { 'fill-color': ['get', 'color'], 'fill-opacity': 0.6 },
        });

        this.map.addLayer({
            id: 'zoneamento-outline',
            type: 'line',
            source: ZONEAMENTO_SOURCE_ID,
            filter: ZONEAMENTO_VISIBLE_FILTER,
            paint: { 'line-color': '#0000ff', 'line-width': 1, 'line-opacity': 0.5 },
        });

        const zoneamentoPopup = new mapboxgl.Popup({ closeButton: true, closeOnClick: true });
        const labels = this.zoneamentoService.properties;

        this.map.on('click', 'zoneamento-fill', (e) => {
            const f = e.features?.[0];
            if (!f) return;
            const p = f.properties || {};

            let body = '';
            Object.entries(labels).forEach(([key, label]) => {
                const value = p[key];
                body += `
                    <div class="info-window-item">
                        <span class="info-window-item-label font-weight-bold">${label}:</span>
                        <span class="info-window-item-value">${value ?? ''}</span>
                    </div>`;
            });

            const content = `
                <div class="info-window">
                    <div class="info-window-header">
                        <h5 class="info-window-title">SIGLA ZONEAMENTO</h5>
                        <h6 class="info-window-subtitle">${p.sigla_zoneamento ?? ''}</h6>
                    </div>
                    <div class="info-window-body" style="max-height:400px;overflow-y:auto;">${body}</div>
                </div>`;

            zoneamentoPopup.setLngLat(e.lngLat).setHTML(content).addTo(this.map);
        });

        this.map.on('mouseenter', 'zoneamento-fill', () => { this.map.getCanvas().style.cursor = 'pointer'; });
        this.map.on('mouseleave', 'zoneamento-fill', () => { this.map.getCanvas().style.cursor = ''; });
    };

    // Obtém os dados de zoneamento da cidade (via cache de instância ou API externa),
    // já processados por prepareData (cores/legenda/opções de filtro).
    getZoneamentoData = async (cidadeOption) => {
        const value = cidadeOption.value;
        if (this.zoneamentoCache[value]) return this.zoneamentoCache[value];

        const parsed = this.parseCidadeValue(value);
        if (!parsed) return null;

        const res = await this.zoneamentoService.getZoneamento({ cidade: parsed.cidade, estado: parsed.uf });
        if (res.status !== 200 || !res.data?.features) return null;

        const prepared = this.zoneamentoService.prepareData(res.data);
        this.zoneamentoCache[value] = prepared;
        return prepared;
    };

    showZoneamentoLayer = async () => {
        if (!this.map?.getSource(ZONEAMENTO_SOURCE_ID)) return;

        const cidades = this.formFiltersRef.current?.getFilters?.()?.cidades || [];
        if (cidades.length === 0) {
            toast.info('Selecione e busque uma cidade para ver o zoneamento.');
            this.setState({ showZoneamento: false });
            return;
        }

        // Zoneamento é por cidade única (mesma regra do mapa Google: cidade[0]).
        const cidadeOption = cidades[0];

        try {
            const prepared = await this.getZoneamentoData(cidadeOption);
            if (!prepared || !prepared.geojson?.features?.length) {
                toast.info('Nenhum dado de zoneamento encontrado para a cidade selecionada.');
                this.setState({ showZoneamento: false });
                return;
            }

            this.currentZoneamentoCidade = cidadeOption;
            this.map.getSource(ZONEAMENTO_SOURCE_ID).setData(prepared.geojson);

            this.setState({
                legendaZoneamento: prepared.legenda,
                opcoesPropriedadesZoneamento: prepared.opcoesDropdownProperties,
                valoresPropriedadesZoneamento: prepared.valoresPropriedades,
                active_tab: 'legendas',
            });
        } catch (e) {
            console.error('[MapboxMap] erro ao carregar zoneamento:', e);
            toast.error('Erro ao carregar o zoneamento.');
            this.setState({ showZoneamento: false });
        }
    };

    // Recorta as zonas exibidas por propriedade/valor (permissao_horizontal/vertical),
    // alternando `visible` em cada feature; o filtro de camada oculta as não-visíveis.
    handleFiltrarZoneamento = ({ property, value }) => {
        const cidadeOption = this.currentZoneamentoCidade;
        const prepared = cidadeOption && this.zoneamentoCache[cidadeOption.value];
        if (!prepared?.geojson) return;

        prepared.geojson.features.forEach((feature) => {
            feature.properties.visible = feature.properties[property] === value;
        });

        this.map.getSource(ZONEAMENTO_SOURCE_ID)?.setData(prepared.geojson);
    };

    removeZoneamentoLayer = () => {
        this.map?.getSource(ZONEAMENTO_SOURCE_ID)?.setData(EMPTY_FC);
        this.setState({
            legendaZoneamento: [],
            opcoesPropriedadesZoneamento: [],
            valoresPropriedadesZoneamento: {},
        });
    };

    toggleShowZoneamento = (enabled) => {
        this.setState({ showZoneamento: enabled });
        if (enabled) {
            this.showZoneamentoLayer();
        } else {
            this.removeZoneamentoLayer();
        }
    };

    // Verifica se a 1ª cidade selecionada tem zoneamento cadastrado (POST /zone-available),
    // gateando a exibição do switch. Espelha refreshBairrosAvailability.
    refreshZoneamentoAvailability = async (cidades) => {
        if (!CAN_VER_ZONEAMENTO) return;

        const cidadeOption = (cidades || [])[0];
        const parsed = cidadeOption && this.parseCidadeValue(cidadeOption.value);
        if (!parsed) {
            this.setState({ zoneamentoAvailable: false });
            return;
        }

        try {
            const res = await this.zoneamentoService.getZoneAvailability({ cidade: parsed.cidade, estado: parsed.uf });
            const available = !!res.data?.available;
            this.setState({ zoneamentoAvailable: available });

            if (!available && this.state.showZoneamento) {
                this.setState({ showZoneamento: false });
                this.removeZoneamentoLayer();
            }
        } catch (e) {
            console.error('[MapboxMap] erro ao verificar disponibilidade de zoneamento:', e);
            this.setState({ zoneamentoAvailable: false });
        }
    };

    // ===== Dados censitários — handlers =====

    // Camadas que exigem a escolha de uma variável (propriedade) para colorir o mapa.
    camadaRequerPropriedadeParaColorirMapa = () => [
        'renda', 'consumo_valor', 'consumo_porcentagem', 'populacao', 'indice_propriedade', 'domicilios',
    ].includes(this.state.datalayerName);

    camadaRequerAno = () => ['trabalhador'].includes(this.state.datalayerName);
    camadaRequerMultiplosAnos = () => ['expansao_urbana'].includes(this.state.datalayerName);

    onChangeDatalayerName = async (event) => {
        const datalayerType = event.target.value;
        const mapColorPropertiesOptions = LinkagesDataLayerService.getOptionsForColorMap(datalayerType);

        await this.setState({
            datalayerName: datalayerType,
            datalayerFriendlyName: LinkagesDataLayerService.getLayerLabel(datalayerType),
            datalayerColorProperty: null,
            datalayerColorPropertyOptions: mapColorPropertiesOptions,
        });

        if (!datalayerType) {
            this.removeDataLayer();
            return;
        }

        // Estes temas não precisam de nenhuma outra informação para exibição.
        const loadDirect = ['dutovias', 'vazios_urbanos', 'area_preservacao', 'area_urbanizada', 'indicadores_municipio'];
        if (loadDirect.includes(datalayerType)) {
            await this.loadDataLayer();
        }
    };

    onChangeDatalayerColorProperty = async (event) => {
        await this.setState({ datalayerColorProperty: event.value });

        const loadDirect = ['renda', 'consumo_valor', 'consumo_porcentagem', 'populacao', 'domicilios', 'indice_propriedade'];
        if (loadDirect.includes(this.state.datalayerName)) {
            await this.loadDataLayer();
        }
    };

    onChangeDatalayerYear = async (event) => {
        await this.setState({ datalayerYear: event.value });
        await this.loadDataLayer();
    };

    onChangeDatalayerYears = async (event) => {
        await this.setState({ datalayerYears: event.value, datalayerColorProperty: 'ano' });
    };

    onChangeOpacidadePreenchimento = (e, newValue) => {
        this.setState({ datalayerFillOpacity: newValue });
        this.map?.setPaintProperty('datalayer-fill', 'fill-opacity', newValue);
    };

    setDemarcacaoAreas = (value) => {
        this.setState({ show_demarcacao_areas: value });
        if (!this.map?.getLayer('datalayer-outline')) return;
        this.map.setPaintProperty('datalayer-outline', 'line-color',
            value ? '#000000' : ['coalesce', ['get', 'color'], '#cccccc']);
        this.map.setPaintProperty('datalayer-outline', 'line-opacity', value ? 0.5 : 0.1);
    };

    // Chave usada para guardar/buscar as faixas de cor customizadas por camada/variável.
    getColorRangeKey = () => {
        let key = this.state.datalayerName;
        if (this.state.datalayerColorProperty) {
            key = `${this.state.datalayerName}_${this.state.datalayerColorProperty}`;
        }
        return key;
    };

    getCachedDataLayerData = (hash) => this.datalayersDataCache?.[hash]?.[this.state.datalayerName];

    putDataOnCache = (hash, data) => {
        this.datalayersDataCache = {
            ...this.datalayersDataCache,
            [hash]: { ...this.datalayersDataCache[hash], [this.state.datalayerName]: data },
        };
    };

    // Monta o payload das áreas desenhadas + cidades para as APIs Linkages.
    // No MapBox a forma desenhada já é um Polygon (turf), então usamos feature.geometry
    // diretamente (sem circleToPolygon). O `id` da forma casa com o nome da coluna por área.
    buildSensoPayload = (field) => {
        const drawnFeatures = (this.draw?.getAll().features || []).filter(
            (f) => f.geometry?.type === 'Polygon' || f.geometry?.type === 'MultiPolygon'
        );

        const polygons = drawnFeatures.map((f) => ({ id: f.id, geojson: f.geometry }));

        const cidadesRaw = this.formFiltersRef.current?.getFilters?.()?.cidades || [];
        const cidades = cidadesRaw.map((cidade) => {
            const parsed = this.parseCidadeValue(cidade.value) || {};
            return { uf: parsed.uf, cidade: parsed.cidade, id: cidade.value };
        });

        const retorno = { polygons, cidades };
        retorno.camada = this.state.datalayerName;
        retorno.poligonos = retorno.polygons;
        retorno.ano = this.state.datalayerYear;
        retorno.anos = this.state.datalayerYears;
        retorno.field = field !== undefined ? field : this.state.datalayerColorProperty;
        retorno.hash = md5(JSON.stringify(retorno));

        return retorno;
    };

    getDatalayerData = async () => {
        const requestPayload = this.buildSensoPayload();

        const cached = this.getCachedDataLayerData(requestPayload.hash);
        if (cached) return cached;

        const response = await LinkagesDataLayerService.getDataLayer(requestPayload);
        if (response.status === 200) {
            this.putDataOnCache(requestPayload.hash, response.data);
        }
        return response.data;
    };

    // Carrega a camada censitária selecionada e a exibe colorida no mapa.
    loadDataLayer = async () => {
        if (!this.state.datalayerName || !this.map?.getSource(DATALAYER_SOURCE_ID)) return;

        await this.setState({ loadingDatalayer: true });

        try {
            const data = await this.getDatalayerData();

            if (!data?.features?.length) {
                toast.warn('Nenhum resultado encontrado para a camada de sociodemografia selecionada.', {
                    position: 'top-center',
                    autoClose: 5000,
                });
                this.removeDataLayer();
                return;
            }

            let rangeColors = LinkagesDataLayerService.getColorRangerForDatalayerProperty(
                this.state.datalayerName,
                this.state.datalayerColorProperty
            );

            const keyColorRange = this.getColorRangeKey();
            if (this.state.color_ranges[keyColorRange]) {
                rangeColors = this.state.color_ranges[keyColorRange];
            }

            const responseData = initializeColors(data, rangeColors, this.state.datalayerColorProperty);

            this.currentDatalayerGeojson = responseData;
            this.map.getSource(DATALAYER_SOURCE_ID).setData(responseData);

            this.setState({
                showLegendaRenda: true,
                active_tab: 'legendas',
                left_side_bar_visible: true,
                color_ranges: { ...this.state.color_ranges, [keyColorRange]: rangeColors },
                colorsOptions: LinkagesDataLayerService.getColorsOptions(this.state.datalayerName),
            });
        } catch (error) {
            console.error('[MapboxMap] erro ao carregar dados censitários:', error);
            toast.error('Erro ao carregar os dados censitários.');
        } finally {
            await this.setState({ loadingDatalayer: false });
        }
    };

    // Remove a camada censitária do mapa e zera a legenda.
    removeDataLayer = () => {
        this.currentDatalayerGeojson = null;
        this.map?.getSource(DATALAYER_SOURCE_ID)?.setData(EMPTY_FC);
        this.setState({ showLegendaRenda: false });
    };

    // Reaplica as faixas de cor editadas no modal de legenda.
    handleAplyColorRanges = async () => {
        if (this.state.datalayerName) {
            await this.loadDataLayer();
        }
    };

    updateRendaColorRange = (colorRange, index) => {
        const key = this.getColorRangeKey();
        const colorRanges = [...(this.state.color_ranges[key] || [])];

        if (index === -1) {
            colorRanges.push(colorRange);
        } else {
            colorRanges[index] = colorRange;
        }

        this.setState({ color_ranges: { ...this.state.color_ranges, [key]: colorRanges } });
    };

    replaceColorRanges = (colorRanges) => {
        const key = this.getColorRangeKey();
        this.setState({ color_ranges: { ...this.state.color_ranges, [key]: [...colorRanges] } });
    };

    removeRendaColorRange = (index) => {
        const key = this.getColorRangeKey();
        const colorRanges = [...(this.state.color_ranges[key] || [])];
        colorRanges.splice(index, 1);
        this.setState({ color_ranges: { ...this.state.color_ranges, [key]: colorRanges } });
    };

    openModalLegendaSetores = () => this.setState({ show_modal_customizar_cores_setores: true });
    closeModalLegendaSetores = () => this.setState({ show_modal_customizar_cores_setores: false });

    // Tabela de dados agregados por cidade/área (botão "Tabela de Dados").
    loadDataLayerAggregated = async () => {
        if (!this.state.datalayerName) return;

        await this.setState({ loadingDatalayer: true });
        try {
            const requestPayload = this.buildSensoPayload(null);
            const response = await LinkagesDataLayerService.getAggregatedData(requestPayload);

            if (response.status === 200 && response.data.length) {
                this.setState({
                    show_modal_datalayer_datatable: true,
                    datalayers_table_data: response.data,
                });
            } else {
                toast.info('Nenhum dado agregado encontrado para a seleção atual.');
            }
        } catch (e) {
            console.error('[MapboxMap] erro ao carregar tabela de dados censitários:', e);
            toast.error('Erro ao carregar a tabela de dados.');
        } finally {
            await this.setState({ loadingDatalayer: false });
        }
    };

    closeModalDatalayerTable = () => this.setState({ show_modal_datalayer_datatable: false });

    // Busca os anos disponíveis para camadas que dependem do ano (Trabalhador, Expansão Urbana).
    loadYearsOptionsForLinkagesDataLayer = async (cidades) => {
        try {
            const response = await LinkagesDataLayerService.getYearsForDataLayer({
                cidades: (cidades || []).map((c) => c.label),
                estados: [],
            });

            if (response.status === 200) {
                const datalayerYearOptions = {};
                Object.keys(response.data).forEach((layerName) => {
                    const years = response.data[layerName] || [];
                    datalayerYearOptions[layerName] = years.map((year) => ({ value: year, label: year.toString() }));
                });
                this.setState({ datalayerYearOptions });
            }
        } catch (e) {
            console.error('[MapboxMap] erro ao carregar anos das camadas censitárias:', e);
        }
    };

    // Forma usada pelo LinkagesDataTableModal/GenericDataFormatter para nomear as colunas
    // por área. O `id` precisa casar com o `id` enviado em buildSensoPayload (feature.id).
    overlaysForDatalayerTable = () => this.state.drawnShapes.map((s) => ({
        id: s.drawId,
        type: s.type,
        name: s.name,
    }));

    setMapStyle = (style) => {
        if (style !== 'light' && style !== 'dark') return;
        this.setState({ mapStyle: style });
        try {
            localStorage.setItem(MAP_STYLE_STORAGE_KEY, style);
        } catch (e) {
            console.warn('[MapboxMap] localStorage indisponível:', e);
        }
        const drawnShapes = this.draw?.getAll();
        this.map.setStyle(MAP_STYLES[style]);
        this.map.once('style.load', () => {
            this.initializeLayers();
            if (drawnShapes?.features?.length) {
                this.draw.set(drawnShapes);
                this.applyShapeFilter();
            }
        });
    };

    componentWillUnmount() {
        if (this.map) this.map.remove();
    }

    // Captura de tela do mapa. MapBox desenha em canvas WebGL, então usamos
    // map.getCanvas().toDataURL() (requer preserveDrawingBuffer: true). Apenas o
    // conteúdo do mapa (basemap + marcadores + bairros) é capturado — controles e
    // sidebar são DOM sobreposto e ficam de fora.
    printMap = async () => {
        if (!this.map) return;

        // Garante que o último frame foi desenhado antes de ler os pixels.
        await new Promise((resolve) => {
            this.map.once('idle', resolve);
            this.map.triggerRepaint();
        });

        const canvas = this.map.getCanvas();
        this.setState({
            mapCaptureCanvas: canvas,
            mapCaptureData: canvas.toDataURL('image/jpeg', 0.95),
            show_map_capture: true,
        });
    };

    closeMapCapture = () => this.setState({ show_map_capture: false });

    loadMarkerImages = () =>
        Promise.all(
            ICON_NAMES.map(
                (name) =>
                    new Promise((resolve) => {
                        if (this.map.hasImage(name)) return resolve();
                        this.map.loadImage(`/img/markers/${name}.png`, (err, image) => {
                            if (err || !image) return resolve();
                            if (!this.map.hasImage(name)) this.map.addImage(name, image);
                            resolve();
                        });
                    })
            )
        );

    // Carrega os 8 marcadores coloridos do mapa de calor sob nomes heat_{HEX}.
    loadHeatMarkerImages = () =>
        Promise.all(
            HEAT_COLORS.map(
                (hex) =>
                    new Promise((resolve) => {
                        const name = `heat_${hex}`;
                        if (this.map.hasImage(name)) return resolve();
                        this.map.loadImage(`/img/marcadores/mapa_de_calor/${hex}.png`, (err, image) => {
                            if (err || !image) return resolve();
                            if (!this.map.hasImage(name)) this.map.addImage(name, image);
                            resolve();
                        });
                    })
            )
        );

    // Fonte dedicada (sem cluster) + camadas de calor. Iniciam ocultas; addHeatMap
    // as popula e exibe. Mantê-las separadas evita conflito com o clustering da
    // fonte principal e permite pesos/recoloração por ponto.
    setupHeatLayers = () => {
        if (this.map.getSource(HEAT_SOURCE_ID)) return;

        this.map.addSource(HEAT_SOURCE_ID, { type: 'geojson', data: EMPTY_FC });

        this.map.addLayer({
            id: 'heatmap-layer',
            type: 'heatmap',
            source: HEAT_SOURCE_ID,
            layout: { visibility: 'none' },
            paint: {
                'heatmap-weight': 1,
                'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 0, 1, 15, 3],
                'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 0, 4, 15, 30],
                'heatmap-opacity': 0.5,
            },
        });

        this.map.addLayer({
            id: 'heat-points',
            type: 'symbol',
            source: HEAT_SOURCE_ID,
            layout: {
                visibility: 'none',
                'icon-image': ['get', 'heatIcon'],
                'icon-size': 0.6,
                'icon-allow-overlap': true,
                'icon-anchor': 'bottom',
            },
        });
    };

    // Constrói a config da legenda (8 faixas) espelhando getHeatMapWeights de DefaulMap.jsx.
    buildHeatLegendConfig = (percentis, metric) => {
        const isCurrency = metric === 'vgv_final' || metric === 'ticket_medio';
        const increment = ['oferta_final', 'oferta_inicial'].includes(metric) ? 1 : 0.1;
        const fmt = (v) =>
            isCurrency ? Number(v.toFixed(2)).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : v;

        const config = [];
        let prev = 0;
        percentis.forEach((p, index) => {
            const min = index === 0 ? 0 : percentis[prev] + increment;
            config.push({
                min: fmt(min),
                max: fmt(p),
                icon: `/img/marcadores/mapa_de_calor/${HEAT_COLORS[index]}.png`,
            });
            prev = index;
        });

        config.push({
            min: fmt(percentis[percentis.length - 1] + increment),
            max: undefined,
            icon: `/img/marcadores/mapa_de_calor/${HEAT_COLORS[HEAT_COLORS.length - 1]}.png`,
        });

        return config;
    };

    // Retorna o índice do bucket (0..7) de um valor conforme os percentis.
    heatBucketIndex = (value, percentis) => {
        if (value <= percentis[0]) return 0;
        if (value > percentis[6]) return 7;
        for (let i = 1; i <= 6; i++) {
            if (value > percentis[i - 1] && value <= percentis[i]) return i;
        }
        return 7;
    };

    addHeatMap = (config) => {
        if (!this.map?.getSource(HEAT_SOURCE_ID) || !this.currentGeojson) return;

        const metric = config.type?.value;
        if (!metric) return;

        this.heatMapConfig = config;

        let features = this.currentGeojson.features || [];

        // Para "Lançamentos", filtra pelo intervalo de datas escolhido.
        if (metric === 'oferta_inicial' && config.data_inicial && config.data_final) {
            const start = new Date(config.data_inicial);
            const end = new Date(config.data_final);
            features = features.filter((f) => {
                const d = f.properties?.data_lancamento;
                if (!d) return false;
                const date = new Date(d);
                return date >= start && date <= end;
            });
        }

        const values = features.map((f) => Number(f.properties?.[metric]) || 0);
        if (features.length === 0) {
            toast.info('Nenhum empreendimento com dados para a métrica selecionada.');
            return;
        }

        const rows = percentile(HEAT_PERCENTILES, features, (f) => Number(f.properties?.[metric]) || 0);
        const percentis = rows.map((r) => Number(r.properties?.[metric]) || 0);

        const heatFeatures = features.map((f) => {
            const value = Number(f.properties?.[metric]) || 0;
            const bucket = this.heatBucketIndex(value, percentis);
            return {
                ...f,
                properties: {
                    ...f.properties,
                    heatWeight: value,
                    heatIcon: HEAT_ICON_NAMES[bucket],
                },
            };
        });

        this.map.getSource(HEAT_SOURCE_ID).setData({ type: 'FeatureCollection', features: heatFeatures });

        // heatmap-weight: normaliza o valor da métrica entre min e max reais.
        const min = Math.min(...values);
        const max = Math.max(...values);
        if (max > min) {
            this.map.setPaintProperty('heatmap-layer', 'heatmap-weight', [
                'interpolate', ['linear'], ['get', 'heatWeight'], min, 0, max, 1,
            ]);
        } else {
            this.map.setPaintProperty('heatmap-layer', 'heatmap-weight', 1);
        }

        HEAT_LAYERS.forEach((id) => this.map.setLayoutProperty(id, 'visibility', 'visible'));
        CLUSTER_LAYERS.forEach((id) => this.map.setLayoutProperty(id, 'visibility', 'none'));

        this.setState({
            showHeatMap: true,
            heatMapWeightType: metric,
            heatMapLegendConfig: this.buildHeatLegendConfig(percentis, metric),
            active_tab: 'legendas',
        });
    };

    removeHeatMap = () => {
        this.heatMapConfig = null;
        if (this.map?.getSource(HEAT_SOURCE_ID)) {
            this.map.getSource(HEAT_SOURCE_ID).setData(EMPTY_FC);
            HEAT_LAYERS.forEach((id) => this.map.setLayoutProperty(id, 'visibility', 'none'));
            // Restaura os marcadores respeitando a preferência de exibição.
            this.setEmpreendimentosVisibility(this.state.showEmpreendimentos);
        }
        this.setState({ showHeatMap: false, heatMapWeightType: null, heatMapLegendConfig: [] });
    };

    setupClusterLayers = () => {
        this.map.addSource(SOURCE_ID, {
            type: 'geojson',
            data: { type: 'FeatureCollection', features: [] },
            cluster: true,
            clusterMaxZoom: 14,
            clusterRadius: 50,
        });

        this.map.addLayer({
            id: 'clusters',
            type: 'circle',
            source: SOURCE_ID,
            filter: ['has', 'point_count'],
            paint: {
                'circle-color': ['step', ['get', 'point_count'], '#51bbd6', 50, '#f1f075', 200, '#f28cb1'],
                'circle-radius': ['step', ['get', 'point_count'], 18, 50, 24, 200, 30],
                'circle-stroke-width': 2,
                'circle-stroke-color': '#ffffff',
            },
        });

        this.map.addLayer({
            id: 'cluster-count',
            type: 'symbol',
            source: SOURCE_ID,
            filter: ['has', 'point_count'],
            layout: {
                'text-field': ['get', 'point_count_abbreviated'],
                'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                'text-size': 13,
            },
        });

        this.map.addLayer({
            id: 'unclustered-point',
            type: 'symbol',
            source: SOURCE_ID,
            filter: ['!', ['has', 'point_count']],
            layout: {
                // range_icon (marcador por faixa de valor — ModalRanges) tem prioridade
                // sobre o ícone padrão por status/padrão.
                'icon-image': ['coalesce', ['get', 'range_icon'], ['get', 'icon']],
                'icon-size': 0.6,
                'icon-allow-overlap': true,
                'icon-anchor': 'bottom',
            },
        });

        this.map.on('click', 'clusters', (e) => {
            const features = this.map.queryRenderedFeatures(e.point, { layers: ['clusters'] });
            const clusterId = features[0].properties.cluster_id;
            this.map.getSource(SOURCE_ID).getClusterExpansionZoom(clusterId, (err, zoom) => {
                if (err) return;
                this.map.easeTo({ center: features[0].geometry.coordinates, zoom });
            });
        });

        const hoverPopup = new mapboxgl.Popup({
            closeButton: false,
            closeOnClick: false,
            offset: 12,
        });

        this.map.on('mouseenter', 'unclustered-point', (e) => {
            this.map.getCanvas().style.cursor = 'pointer';
            const f = e.features[0];
            const coords = f.geometry.coordinates.slice();
            const p = f.properties;

            const cityState = p.city && p.state ? `${p.city}/${p.state}` : (p.city || p.state || '');
            const addressParts = [p.address, p.neighborhood, cityState].filter((x) => x && x.length);
            const addressLine = addressParts.length ? addressParts.join(', ') : 'Endereço não disponível';

            hoverPopup
                .setLngLat(coords)
                .setHTML(`<strong>${p.name || ''}</strong><br/><small>${addressLine}</small>`)
                .addTo(this.map);
        });

        this.map.on('mouseleave', 'unclustered-point', () => {
            this.map.getCanvas().style.cursor = '';
            hoverPopup.remove();
        });

        // Clique num marcador individual → abre o modal de detalhes do empreendimento.
        this.map.on('click', 'unclustered-point', (e) => {
            const f = e.features?.[0];
            if (!f) return;
            hoverPopup.remove();
            this.openEmpreendimentoModal(f.properties?.id);
        });

        this.map.on('mouseenter', 'clusters', () => { this.map.getCanvas().style.cursor = 'pointer'; });
        this.map.on('mouseleave', 'clusters', () => { this.map.getCanvas().style.cursor = ''; });
    };

    // ===== Polos geradores =====

    // Carrega os 11 ícones de categoria sob o nome `polo_<arquivo>`.
    loadPolosImages = () =>
        Promise.all(
            POLO_ICON_FILES.map(
                (file) =>
                    new Promise((resolve) => {
                        const name = `polo_${file}`;
                        if (this.map.hasImage(name)) return resolve();
                        this.map.loadImage(`/img/polos_geradores/grupos/${file}.png`, (err, image) => {
                            if (err || !image) return resolve();
                            // pixelRatio: 2 → o PNG de 64px é tratado como ícone de 32px @2x
                            // (nítido em telas retina, sem serrilhar por minificação).
                            if (!this.map.hasImage(name)) this.map.addImage(name, image, { pixelRatio: 2 });
                            resolve();
                        });
                    })
            )
        );

    setupPolosLayers = () => {
        if (this.map.getSource(POLOS_SOURCE_ID)) return;

        this.map.addSource(POLOS_SOURCE_ID, {
            type: 'geojson',
            data: EMPTY_FC,
            cluster: true,
            clusterMaxZoom: 14,
            clusterRadius: 50,
        });

        this.map.addLayer({
            id: 'polos-clusters',
            type: 'circle',
            source: POLOS_SOURCE_ID,
            filter: ['has', 'point_count'],
            layout: { visibility: 'none' },
            paint: {
                'circle-color': ['step', ['get', 'point_count'], '#7e57c2', 50, '#5e35b1', 200, '#4527a0'],
                'circle-radius': ['step', ['get', 'point_count'], 16, 50, 22, 200, 28],
                'circle-stroke-width': 2,
                'circle-stroke-color': '#ffffff',
            },
        });

        this.map.addLayer({
            id: 'polos-cluster-count',
            type: 'symbol',
            source: POLOS_SOURCE_ID,
            filter: ['has', 'point_count'],
            layout: {
                visibility: 'none',
                'text-field': ['get', 'point_count_abbreviated'],
                'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                'text-size': 13,
            },
            paint: { 'text-color': '#ffffff' },
        });

        this.map.addLayer({
            id: 'polos-unclustered-point',
            type: 'symbol',
            source: POLOS_SOURCE_ID,
            filter: ['!', ['has', 'point_count']],
            layout: {
                visibility: 'none',
                'icon-image': ['get', 'icon'],
                // Imagens registradas com pixelRatio 2 (64px = 32px @2x) → icon-size 1.
                'icon-size': 1,
                'icon-allow-overlap': true,
                'icon-anchor': 'bottom',
            },
        });

        this.map.on('click', 'polos-clusters', (e) => {
            const features = this.map.queryRenderedFeatures(e.point, { layers: ['polos-clusters'] });
            const clusterId = features[0].properties.cluster_id;
            this.map.getSource(POLOS_SOURCE_ID).getClusterExpansionZoom(clusterId, (err, zoom) => {
                if (err) return;
                this.map.easeTo({ center: features[0].geometry.coordinates, zoom });
            });
        });

        const poloPopup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: 12 });

        this.map.on('mouseenter', 'polos-unclustered-point', (e) => {
            this.map.getCanvas().style.cursor = 'pointer';
            const f = e.features[0];
            const coords = f.geometry.coordinates.slice();
            const p = f.properties;
            const address = p.address && p.address.length ? p.address : 'Endereço não disponível';
            poloPopup
                .setLngLat(coords)
                .setHTML(`<strong>${p.title || ''}</strong><br/><small>${address}</small>`)
                .addTo(this.map);
        });

        this.map.on('mouseleave', 'polos-unclustered-point', () => {
            this.map.getCanvas().style.cursor = '';
            poloPopup.remove();
        });

        this.map.on('mouseenter', 'polos-clusters', () => { this.map.getCanvas().style.cursor = 'pointer'; });
        this.map.on('mouseleave', 'polos-clusters', () => { this.map.getCanvas().style.cursor = ''; });
    };

    // Busca os polos das cidades selecionadas e popula o cache `allPolos`.
    // `types` é passado explicitamente para evitar corrida com o setState da seleção.
    fetchPolos = async (cidades, types = this.state.polosTypesSelected) => {
        if (!cidades || cidades.length === 0) return;

        const city = [];
        const state = [];
        cidades.forEach((c) => {
            const parsed = this.parseCidadeValue(c.value);
            if (!parsed) return;
            city.push(parsed.cidade);
            if (!state.includes(parsed.uf)) state.push(parsed.uf);
        });

        const payload = { city, state };
        if (types && types.length) payload.types = types;

        try {
            // Endpoint enxuto do mapa novo: já devolve GeoJSON pronto (com `icon`).
            const res = await axios.post('/api/polos-geradores-mapbox', payload);
            this.allPolos = res.data.features || [];

            this.setState({ polosLoaded: true });
            this.applyPolosShapeFilter();

            if (this.allPolos.length === 0) {
                toast.info('Nenhum polo gerador encontrado para os filtros selecionados.');
            }
        } catch (e) {
            console.error('[MapboxMap] erro ao carregar polos geradores:', e);
            toast.error('Erro ao carregar polos geradores.');
        }
    };

    // Recorta os polos pelas formas desenhadas (se houver) e atualiza a source.
    applyPolosShapeFilter = () => {
        if (!this.map?.getSource(POLOS_SOURCE_ID)) return;

        const shapes = (this.draw?.getAll().features || []).filter(
            (f) => f.geometry?.type === 'Polygon' || f.geometry?.type === 'MultiPolygon'
        );

        let filtered = this.allPolos;
        if (shapes.length > 0) {
            filtered = this.allPolos.filter((point) =>
                shapes.some((shape) => {
                    try {
                        return booleanPointInPolygon(point, shape);
                    } catch {
                        return false;
                    }
                })
            );
        }

        this.map.getSource(POLOS_SOURCE_ID).setData({ type: 'FeatureCollection', features: filtered });
    };

    setPolosLayersVisibility = (visible) => {
        const value = visible ? 'visible' : 'none';
        POLOS_LAYERS.forEach((id) => {
            if (this.map?.getLayer(id)) this.map.setLayoutProperty(id, 'visibility', value);
        });
    };

    // ===== Exibir/esconder marcadores de empreendimentos =====
    setEmpreendimentosVisibility = (visible) => {
        const value = visible ? 'visible' : 'none';
        CLUSTER_LAYERS.forEach((id) => {
            if (this.map?.getLayer(id)) this.map.setLayoutProperty(id, 'visibility', value);
        });
    };

    toggleEmpreendimentos = (enabled) => {
        this.setState({ showEmpreendimentos: enabled });
        // Com heatmap ativo, as camadas de cluster já ficam ocultas por design;
        // a preferência é reaplicada ao remover o heatmap (removeHeatMap).
        if (!this.state.showHeatMap) {
            this.setEmpreendimentosVisibility(enabled);
        }
    };

    togglePolos = async (enabled) => {
        this.setState({ showPolosGeradores: enabled });
        this.setPolosLayersVisibility(enabled);

        if (enabled && this.allPolos.length === 0) {
            const cidades = this.formFiltersRef.current?.getFilters?.()?.cidades || [];
            if (cidades.length === 0) {
                toast.info('Selecione e busque uma cidade para carregar os polos geradores.');
                return;
            }
            await this.fetchPolos(cidades);
        }
    };

    applyPolosTypes = async (types) => {
        this.setState({ polosTypesSelected: types, polosModalOpen: false });
        if (!this.state.showPolosGeradores) return;
        const cidades = this.formFiltersRef.current?.getFilters?.()?.cidades || [];
        if (cidades.length > 0) await this.fetchPolos(cidades, types);
    };

    setupGooglePlaces = () => {
        loadGoogleMapsPlaces()
            .then(() => {
                if (!this.searchInputRef.current) {
                    return;
                }

                const autocomplete = new window.google.maps.places.Autocomplete(this.searchInputRef.current, {
                    fields: ['geometry', 'formatted_address', 'name'],
                    componentRestrictions: { country: 'br' },
                });
                autocomplete.addListener('place_changed', async () => {
                    const place = autocomplete.getPlace();
                    if (!place.geometry?.location) return;
                    const lng = place.geometry.location.lng();
                    const lat = place.geometry.location.lat();
                    this.map.flyTo({ center: [lng, lat], zoom: SELECTED_ZOOM, essential: true });
                    this.setAddressMarker(lat, lng, place.formatted_address || place.name);
                    await this.loadFromAddress(lat, lng, [place]);
                });
                this.searchInputRef.current.addEventListener('keydown', (e) => {
                    if (e.key === 'Enter') e.preventDefault();
                });
            })
            .catch((err) => console.error('[MapboxMap] falha ao carregar Google Places:', err));
    };

    loadFromAddress = async (lat, lng, place) => {
        let reverse;
        try {
            reverse = await geolocationService.reverse(lat, lng, place);
        } catch {
            toast.error('Não foi possível identificar a localidade.');
            return;
        }
        const uf = reverse?.uf;
        const cidade = reverse?.cidade;
        if (!uf || !cidade) {
            toast.warn('Localidade sem cidade/UF identificável.');
            return;
        }

        const cidadeOption = { value: `${uf}-${cidade}`, label: `${cidade} - ${uf}` };
        this.setState({ cidadeSincronizada: [cidadeOption] });

        // Reaproveita os filtros atuais do formulário, forçando a cidade do endereço buscado
        const currentFilters = this.formFiltersRef.current?.getFilters?.() || {};
        const filters = { ...currentFilters, cidades: [cidadeOption] };

        const payload = this.buildFiltersPayload(filters);

        await Promise.all([
            this.fetchAndRender(payload),
            this.fetchAnalysis(payload, `${cidade} - ${uf}`),
        ]);

        this.refreshBairrosAvailability([cidadeOption]);
        if (this.state.showBairros) {
            this.renderBairros([cidadeOption]);
        }
        this.refreshZoneamentoAvailability([cidadeOption]);
        this.loadYearsOptionsForLinkagesDataLayer([cidadeOption]);
    };

    buildFiltersPayload = (filters) => {
        const { cidades, status, tipo, padrao, ...rest } = filters;

        const formatDate = (d) => {
            if (!d || !(d instanceof Date)) return null;
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const yyyy = d.getFullYear();
            return `${mm}/${yyyy}`;
        };

        const nonEmpty = (v) => v !== '' && v !== null && v !== undefined && v !== 0;

        const payload = {
            cidades: cidades.map((c) => c.value),
            // Busca por estado: as UFs só valem quando nenhuma cidade foi selecionada —
            // com cidade escolhida, o Estado serve apenas para filtrar o select de cidades.
            estados: cidades.length === 0 ? (rest.estados || []) : [],
            status: status?.length ? status : ['Ativo'],
            tipo: tipo || [],
            padrao: padrao || [],
            bairros: rest.bairros || [],
            incorporadoras: (rest.incorporadoras || []).map((i) => i.value),
            nome_empreendimento: (rest.nome_empreendimento || []).map((n) => n.value),
            quartos: (rest.quartos || []).filter((q) => typeof q === 'number'),
            ano_lancamento: rest.ano_lancamento || [],
            estagio: rest.estagio || [],
            retrofit: rest.retrofit || [],
            check_incluir_monitoradas: this.state.incluirNaoMonitoradas,
        };

        const scalars = {
            area_maior_que: rest.area_maior_que,
            area_menor_que: rest.area_menor_que,
            valor_maior_que: rest.valor_maior_que,
            valor_menor_que: rest.valor_menor_que,
            valor_metro_maior_que: rest.valor_metro_maior_que,
            valor_metro_menor_que: rest.valor_metro_menor_que,
            porcentagem_vendida_minima: rest.porcentagem_vendida_minima,
            porcentagem_vendida_maxima: rest.porcentagem_vendida_maxima,
            mes_ano_lancamento: formatDate(rest.mes_ano_lancamento),
            juros_minimo: rest.juros_minimo,
            juros_maximo: rest.juros_maximo,
            parcelas_minimo: rest.parcelas_minimo,
            parcelas_maximo: rest.parcelas_maximo,
            valor_parcela_minima: rest.valor_parcela_minima,
            valor_parcela_maxima: rest.valor_parcela_maxima,
        };
        Object.entries(scalars).forEach(([key, val]) => {
            if (nonEmpty(val)) payload[key] = val;
        });

        return payload;
    };

    handleFiltersSubmit = async (filters) => {
        const { cidades, estados } = filters;
        const buscaPorEstado = (!cidades || cidades.length === 0) && (estados || []).length > 0;
        if ((!cidades || cidades.length === 0) && !buscaPorEstado) {
            toast.warn('Selecione ao menos uma cidade ou um estado.');
            return;
        }

        const payload = this.buildFiltersPayload(filters);
        const areaName = buscaPorEstado
            ? estados.join(', ')
            : cidades.map((c) => c.label || c.value).join(', ');

        await Promise.all([
            this.fetchAndRender(payload),
            this.fetchAnalysis(payload, areaName),
            this.state.showPolosGeradores ? this.fetchPolos(cidades) : Promise.resolve(),
        ]);

        // Na busca por estado não há cidade/endereço para posicionar a câmera:
        // enquadra nos resultados carregados.
        if (buscaPorEstado) {
            this.fitBoundsToLoadedFeatures();
        }

        this.refreshBairrosAvailability(cidades);
        if (this.state.showBairros) {
            this.renderBairros(cidades);
        }
        this.refreshZoneamentoAvailability(cidades);
        // Cidade pode ter mudado: reaplica o zoneamento se estiver ligado.
        if (this.state.showZoneamento) {
            this.showZoneamentoLayer();
        }
        this.loadYearsOptionsForLinkagesDataLayer(cidades);
    };

    handleFiltersClear = () => {
        if (this.state.pickingIsochroneCenter) this.toggleIsochronePick();
        this.removeAddressMarker();
        this.currentGeojson = null;
        this.allFeatures = [];
        this.removeHeatMap();
        this.draw?.deleteAll();
        if (this.map?.getSource(SOURCE_ID)) {
            this.map.getSource(SOURCE_ID).setData({ type: 'FeatureCollection', features: [] });
        }
        if (this.map?.getSource(BAIRROS_SOURCE_ID)) {
            this.map.getSource(BAIRROS_SOURCE_ID).setData(EMPTY_FC);
        }
        this.allPolos = [];
        if (this.map?.getSource(POLOS_SOURCE_ID)) {
            this.map.getSource(POLOS_SOURCE_ID).setData(EMPTY_FC);
        }
        this.setPolosLayersVisibility(false);
        this.setEmpreendimentosVisibility(true);
        if (this.map?.getSource(ZONEAMENTO_SOURCE_ID)) {
            this.map.getSource(ZONEAMENTO_SOURCE_ID).setData(EMPTY_FC);
        }
        this.zoneamentoCache = {};
        this.currentZoneamentoCidade = null;
        if (this.map?.getSource(DATALAYER_SOURCE_ID)) {
            this.map.getSource(DATALAYER_SOURCE_ID).setData(EMPTY_FC);
        }
        this.currentDatalayerGeojson = null;
        this.datalayersDataCache = {};
        if (this.searchInputRef.current) {
            this.searchInputRef.current.value = '';
        }
        this.setState({ totalEmpreendimentos: 0, cidadeSincronizada: null, searchAreas: [], drawnShapes: [], right_panel_visible: false, empreendimentoDetail: null, showEmpreendimentoModal: false, showBairros: false, bairrosAvailable: false, showPolosGeradores: false, polosLoaded: false, polosTypesSelected: [], showEmpreendimentos: true, incluirNaoMonitoradas: false, showZoneamento: false, zoneamentoAvailable: false, legendaZoneamento: [], opcoesPropriedadesZoneamento: [], valoresPropriedadesZoneamento: {}, datalayerName: '', datalayerFriendlyName: null, datalayerColorProperty: null, datalayerColorPropertyOptions: [], datalayerYear: null, datalayerYears: [], showLegendaRenda: false, show_demarcacao_areas: false, showRaioDeslocamento: false, isochroneCenter: null, ranges: [], modalRangesOpen: false, loadedProjectId: null, modalSalvarProjetoOpen: false });
    };

    handleRemoveArea = (areaId) => {
        this.setState((prev) => {
            const updated = prev.searchAreas.filter((a) => a.id !== areaId);
            return { searchAreas: updated, right_panel_visible: updated.length > 0 };
        });
    };

    // Ao selecionar um único estado no filtro, centraliza o mapa na capital (paridade com
    // o DefaulMap.centralizarMapaNaCapital do mapa Google). Com múltiplas UFs não há um
    // centro óbvio; o enquadramento acontece após a busca (fitBoundsToLoadedFeatures).
    centralizarMapaNaCapital = async (estados) => {
        if (!this.map || !estados || estados.length !== 1) return;

        try {
            const res = await axios.get(route('state.capital', { state: estados[0] }));
            const { lat, lng } = res.data || {};
            if (lat == null || lng == null) return;

            this.map.flyTo({ center: [Number(lng), Number(lat)], zoom: 12 });
        } catch (e) {
            console.error('[MapboxMap] erro ao centralizar na capital do estado:', e);
        }
    };

    // Enquadra o mapa nos empreendimentos atualmente exibidos (pós-recorte por formas).
    fitBoundsToLoadedFeatures = () => {
        const features = this.currentGeojson?.features || [];
        if (!this.map || features.length === 0) return;

        const bounds = new mapboxgl.LngLatBounds();
        features.forEach((f) => {
            const coords = f.geometry?.coordinates;
            if (coords) bounds.extend(coords);
        });

        if (!bounds.isEmpty()) {
            this.map.fitBounds(bounds, { padding: 60, duration: 600 });
        }
    };

    fetchAndRender = async (payload) => {
        try {
            const res = await axios.post('/api/empreendimentos-mapbox', payload);
            const geojson = res.data;

            this.allFeatures = geojson.features || [];
            if (this.state.ranges.length > 0) {
                await this.ensureRangeImages();
                this.tagFeaturesWithRanges();
            }
            this.applyShapeFilter();

            const total = this.state.totalEmpreendimentos;
            if (total === 0) toast.info('Nenhum empreendimento encontrado para os filtros selecionados.');
            else toast.success(`${total} empreendimento(s) carregado(s).`);
        } catch (e) {
            console.error('[MapboxMap] erro:', e);
            toast.error('Erro ao carregar empreendimentos.');
        }
    };

    fetchAnalysis = async (payload, areaName) => {
        const color = colors[this.state.searchAreas.length % colors.length];
        const areaId = `area-${Date.now()}`;

        this.setState((prev) => ({
            searchAreas: [...prev.searchAreas, { id: areaId, name: areaName, color, loading: true, meta_data: null }],
            right_panel_visible: true,
        }));

        try {
            const res = await axios.post('/api/empreendimentos-mapbox/analysis', payload);
            const { meta_data } = res.data;

            this.setState((prev) => ({
                searchAreas: prev.searchAreas.map((a) =>
                    a.id === areaId ? { ...a, meta_data, loading: false } : a
                ),
            }));
        } catch (e) {
            console.error('[MapboxMap] erro metadata:', e);
            toast.error('Erro ao carregar dados de análise.');
            this.setState((prev) => ({
                searchAreas: prev.searchAreas.filter((a) => a.id !== areaId),
            }));
        }
    };

    handleToggleBairros = (enabled) => {
        this.setState({ showBairros: enabled });
        if (!this.map?.getSource(BAIRROS_SOURCE_ID)) return;

        if (!enabled) {
            this.map.getSource(BAIRROS_SOURCE_ID).setData(EMPTY_FC);
            return;
        }

        const cidades = this.formFiltersRef.current?.getFilters?.()?.cidades || [];
        if (cidades.length === 0) {
            toast.info('Selecione e busque uma cidade para ver os limites de bairros.');
        }
        this.renderBairros(cidades);
    };

    renderBairros = async (cidades) => {
        if (!this.map?.getSource(BAIRROS_SOURCE_ID)) return;

        const groups = await Promise.all((cidades || []).map((c) => this.fetchBairros(c.value)));
        const features = groups.flat();
        this.map.getSource(BAIRROS_SOURCE_ID)?.setData({ type: 'FeatureCollection', features });
    };

    // value = "UF-Cidade" (a UF vem primeiro; o nome da cidade pode conter '-', ex.: "SP-Embu-Guaçu").
    parseCidadeValue = (value) => {
        if (!value) return null;
        const idx = value.indexOf('-');
        if (idx === -1) return null;
        return { uf: value.slice(0, idx), cidade: value.slice(idx + 1) };
    };

    fetchBairros = async (value) => {
        if (this.bairrosCache[value]) return this.bairrosCache[value];
        const parsed = this.parseCidadeValue(value);
        if (!parsed) return [];

        try {
            const res = await axios.get(`/bairros/geojson/${encodeURIComponent(parsed.cidade)}/${encodeURIComponent(parsed.uf)}`);
            const features = (res.data && res.data.features) || [];
            this.bairrosCache[value] = features;
            return features;
        } catch (e) {
            console.error('[MapboxMap] erro ao carregar limites de bairros:', e);
            return [];
        }
    };

    // Verifica, a partir da lista de cidades com limites cadastrados (injetada no
    // page load via window.bairrosCities), se alguma cidade selecionada possui
    // limites de bairros. Atualiza bairrosAvailable (controla a exibição do switch).
    // Se nenhuma tiver, desliga e limpa eventuais limites já exibidos. Sem AJAX.
    refreshBairrosAvailability = (cidades) => {
        const available = (cidades || []).some((c) => BAIRROS_CITIES.has(c.value));
        this.setState({ bairrosAvailable: available });

        if (!available && this.state.showBairros) {
            this.setState({ showBairros: false });
            this.map?.getSource(BAIRROS_SOURCE_ID)?.setData(EMPTY_FC);
        }
        return available;
    };

    render() {
        const { left_side_bar_visible, right_panel_visible, active_tab, totalEmpreendimentos, cidadeSincronizada, searchAreas } = this.state;

        return (
            <>
                <div
                    ref={this.containerRef}
                    style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
                />

                <input
                    ref={this.searchInputRef}
                    type="text"
                    placeholder="Digite um endereço"
                    style={{
                        position: 'absolute',
                        top: 20,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '40%',
                        minWidth: 280,
                        height: 40,
                        padding: '0 16px',
                        border: '1px solid #cccccc',
                        borderRadius: 4,
                        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
                        fontSize: 14,
                        outline: 'none',
                        background: '#ffffff',
                        zIndex: 1,
                    }}
                />

                {!left_side_bar_visible && (
                    <div className="map-sidebar-show-button-wrapper">
                        <button
                            title="Mostrar Barra Lateral"
                            onClick={() => this.setState({ left_side_bar_visible: true })}
                            className="btn btn-primary btn-show-map-sidebar"
                        >
                            <i className="fas fa-angle-double-right"></i>
                        </button>
                    </div>
                )}

                <Sidebar
                    visible={left_side_bar_visible}
                    onHide={() => this.setState({ left_side_bar_visible: false })}
                    modal={false}
                    dismissable={false}
                >
                    <Tabs
                        activeKey={active_tab}
                        defaultActiveKey="filtros"
                        onSelect={(e) => this.setState({ active_tab: e })}
                        id="mapbox-tabs"
                    >
                        <Tab eventKey="filtros" title="Filtros">
                            <FormFiltersContainer
                                ref={this.formFiltersRef}
                                onSubmit={this.handleFiltersSubmit}
                                onClear={this.handleFiltersClear}
                                onEstadosChanged={this.centralizarMapaNaCapital}
                                onProjetoSelected={this.handleProjetoSelected}
                                cidadeSincronizada={cidadeSincronizada}
                                showLegenda={totalEmpreendimentos > 0}
                            />
                        </Tab>

                        <Tab eventKey="ferramentas" title="Ferramentas">
                            <div className="card-body" style={{ height: '75vh', overflowY: 'auto' }}>
                                <div className="form-group">
                                    <label className="mb-2 d-block">Estilo do mapa</label>
                                    <div className="d-flex align-items-center">
                                        <InputSwitch
                                            inputId="check_dark_mode"
                                            checked={this.state.mapStyle === 'dark'}
                                            onChange={(e) => this.setMapStyle(e.value ? 'dark' : 'light')}
                                        />
                                        <label htmlFor="check_dark_mode" className="pl-1 mb-0 ms-2">
                                            Modo noturno
                                        </label>
                                    </div>
                                    <small className="text-muted d-block mt-1">
                                        A preferência fica salva neste navegador.
                                    </small>
                                </div>

                                <hr />

                                <div className="form-group">
                                    <label className="mb-2 d-block">Empreendimentos</label>
                                    <div className="d-flex align-items-center">
                                        <InputSwitch
                                            inputId="check_empreendimentos"
                                            checked={this.state.showEmpreendimentos}
                                            onChange={(e) => this.toggleEmpreendimentos(e.value)}
                                        />
                                        <label htmlFor="check_empreendimentos" className="pl-1 mb-0 ms-2">
                                            Exibir empreendimentos
                                        </label>
                                    </div>
                                    {window.isCliente === false && (
                                        <div className="d-flex align-items-center mt-2">
                                            <InputSwitch
                                                inputId="check_incluir_monitoradas"
                                                checked={this.state.incluirNaoMonitoradas}
                                                onChange={(e) => this.setState({ incluirNaoMonitoradas: e.value })}
                                            />
                                            <label htmlFor="check_incluir_monitoradas" className="pl-1 mb-0 ms-2">
                                                Incluir cidades não monitoradas
                                            </label>
                                        </div>
                                    )}
                                </div>

                                {this.state.bairrosAvailable && (
                                    <>
                                        <hr />
                                        <div className="form-group">
                                            <label className="mb-2 d-block">Limites de bairros</label>
                                            <div className="d-flex align-items-center">
                                                <InputSwitch
                                                    inputId="check_bairros"
                                                    checked={this.state.showBairros}
                                                    onChange={(e) => this.handleToggleBairros(e.value)}
                                                />
                                                <label htmlFor="check_bairros" className="pl-1 mb-0 ms-2">
                                                    Exibir limites de bairros
                                                </label>
                                            </div>
                                        </div>
                                    </>
                                )}

                                <hr />

                                <div className="form-group">
                                    <label className="mb-2 d-block">Captura de tela do mapa</label>
                                    <button
                                        type="button"
                                        className="btn btn-success btn-block"
                                        onClick={this.printMap}
                                        title="Efetua Captura de Tela do Mapa"
                                    >
                                        Capturar
                                    </button>
                                </div>

                                {this.state.drawnShapes.length > 0 && (
                                    <>
                                        <hr />
                                        <label className="mb-2 d-block">Formas desenhadas</label>
                                        <DrawnShapesList
                                            shapes={this.state.drawnShapes}
                                            onCenter={this.centerOnShape}
                                            onRename={this.renameShape}
                                            onToggleVisibility={this.toggleShapeVisibility}
                                            onSetRadius={this.setShapeRadius}
                                            onEditAttributes={this.editShapeAttributes}
                                            onRemove={this.removeShape}
                                        />
                                    </>
                                )}

                                <hr />

                                <div className="form-group">
                                    <label className="mb-2 d-block">Mapa de calor</label>
                                    {this.state.showHeatMap ? (
                                        <button
                                            type="button"
                                            className="btn btn-outline-danger btn-block"
                                            onClick={this.removeHeatMap}
                                        >
                                            Remover Mapa de Calor
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            className="btn btn-primary btn-block"
                                            onClick={() => this.setState({ modalHeatMapOpen: true })}
                                            disabled={totalEmpreendimentos === 0}
                                            title={totalEmpreendimentos === 0 ? 'Faça uma busca para habilitar' : 'Criar mapa de calor'}
                                        >
                                            Criar Mapa de Calor
                                        </button>
                                    )}
                                </div>

                                <hr />

                                <div className="form-group">
                                    <label className="mb-2 d-block">Polos geradores</label>
                                    <div className="d-flex align-items-center">
                                        <InputSwitch
                                            inputId="check_polos_geradores"
                                            checked={this.state.showPolosGeradores}
                                            onChange={(e) => this.togglePolos(e.value)}
                                        />
                                        <label htmlFor="check_polos_geradores" className="pl-1 mb-0 ms-2">
                                            Exibir polos geradores
                                        </label>
                                    </div>
                                    {this.state.showPolosGeradores && (
                                        <button
                                            type="button"
                                            className="btn btn-outline-primary btn-block mt-2"
                                            onClick={() => this.setState({ polosModalOpen: true })}
                                        >
                                            Selecionar tipos
                                        </button>
                                    )}
                                </div>

                                {CAN_VER_ZONEAMENTO && this.state.zoneamentoAvailable && (
                                    <>
                                        <hr />
                                        <div className="form-group">
                                            <label className="mb-2 d-block">Zoneamento</label>
                                            <div className="d-flex align-items-center">
                                                <InputSwitch
                                                    inputId="check_zoneamento"
                                                    checked={this.state.showZoneamento}
                                                    onChange={(e) => this.toggleShowZoneamento(e.value)}
                                                />
                                                <label htmlFor="check_zoneamento" className="pl-1 mb-0 ms-2">
                                                    Exibir zoneamento
                                                </label>
                                            </div>
                                            {this.state.showZoneamento && (
                                                <FiltroZoneamento
                                                    opcoes={this.state.opcoesPropriedadesZoneamento}
                                                    valores={this.state.valoresPropriedadesZoneamento}
                                                    handleSubmit={this.handleFiltrarZoneamento}
                                                />
                                            )}
                                        </div>
                                    </>
                                )}

                                {this.state.permissoesSociodemografia.length > 0 && (
                                    <>
                                        <hr />
                                        <div className="form-group">
                                            <label className="mb-2 d-block">Dados censitários</label>
                                            <div className="d-flex align-items-center justify-content-between">
                                                <select
                                                    style={{ height: '43px' }}
                                                    className="form-control"
                                                    value={this.state.datalayerName}
                                                    onChange={this.onChangeDatalayerName}
                                                >
                                                    <option value="">Selecione</option>
                                                    {this.state.datalayers.map((layerGroupAndOptions) => (
                                                        <optgroup label={layerGroupAndOptions.group} key={layerGroupAndOptions.group}>
                                                            {layerGroupAndOptions.options.map((option) => (
                                                                <option value={option.value} key={option.value}>{option.label}</option>
                                                            ))}
                                                        </optgroup>
                                                    ))}
                                                </select>
                                                <button
                                                    type="button"
                                                    className="btn btn-primary btn-link ms-2"
                                                    title="Dicionário de Dados"
                                                    onClick={() => this.setState({ show_modal_data_dictionary: true })}
                                                >
                                                    <i className="fa-solid fa-circle-info"></i>
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-primary btn-link"
                                                    title="Tabela de Dados"
                                                    disabled={!this.state.datalayerName}
                                                    onClick={this.loadDataLayerAggregated}
                                                >
                                                    <i className="fas fa-table"></i>
                                                </button>
                                            </div>

                                            {this.camadaRequerPropriedadeParaColorirMapa() && (
                                                <div className="mt-2">
                                                    <label className="mb-1 d-block">Selecione a variável:</label>
                                                    <Dropdown
                                                        options={this.state.datalayerColorPropertyOptions}
                                                        onChange={this.onChangeDatalayerColorProperty}
                                                        style={{ width: '100%' }}
                                                        value={this.state.datalayerColorProperty}
                                                        showClear
                                                        placeholder="Selecione"
                                                        disabled={!this.state.datalayerName}
                                                    />
                                                </div>
                                            )}

                                            {this.camadaRequerAno() && (
                                                <div className="mt-2">
                                                    <label className="mb-1 d-block">Ano:</label>
                                                    <Dropdown
                                                        options={this.state.datalayerYearOptions?.[this.state.datalayerName] || []}
                                                        onChange={this.onChangeDatalayerYear}
                                                        style={{ width: '100%' }}
                                                        value={this.state.datalayerYear}
                                                        showClear
                                                        placeholder="Selecione"
                                                        disabled={!this.state.datalayerName}
                                                    />
                                                </div>
                                            )}

                                            {this.camadaRequerMultiplosAnos() && (
                                                <div className="mt-2">
                                                    <label className="mb-1 d-block">Ano:</label>
                                                    <MultiSelect
                                                        options={this.state.datalayerYearOptions?.[this.state.datalayerName] || []}
                                                        onChange={this.onChangeDatalayerYears}
                                                        display="chip"
                                                        style={{ width: '100%' }}
                                                        value={this.state.datalayerYears}
                                                        placeholder="Selecione"
                                                        disabled={!this.state.datalayerName}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-primary btn-block mt-2"
                                                        onClick={this.loadDataLayer}
                                                        disabled={!this.state.datalayerName || this.state.datalayerYears?.length === 0}
                                                    >
                                                        Carregar Dados
                                                    </button>
                                                </div>
                                            )}

                                            {this.state.datalayerName && (
                                                <div className="d-flex align-items-center mt-3">
                                                    <InputSwitch
                                                        inputId="check_demarcacao_areas"
                                                        checked={this.state.show_demarcacao_areas}
                                                        onChange={(e) => this.setDemarcacaoAreas(e.value)}
                                                    />
                                                    <label htmlFor="check_demarcacao_areas" className="pl-1 mb-0 ms-2">
                                                        Exibir demarcação de áreas
                                                    </label>
                                                </div>
                                            )}

                                            {this.state.loadingDatalayer && (
                                                <small className="text-muted d-block mt-2">Carregando dados…</small>
                                            )}
                                        </div>
                                    </>
                                )}

                                <hr />

                                <div className="form-group">
                                    <label className="mb-2 d-block">Projeto</label>
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary btn-block"
                                        onClick={() => this.setState({ modalSalvarProjetoOpen: true })}
                                        title="Salva filtros, formas e camadas atuais como um projeto (com compartilhamento)"
                                    >
                                        <i className="fas fa-save mr-1"></i> Salvar Projeto
                                    </button>
                                    {this.state.loadedProjectId && (
                                        <button
                                            type="button"
                                            className="btn btn-outline-danger btn-block mt-2"
                                            onClick={this.deleteProject}
                                            title="Exclui o projeto atualmente carregado"
                                        >
                                            <i className="fas fa-trash mr-1"></i> Excluir projeto carregado
                                        </button>
                                    )}
                                </div>

                                <hr />

                                <div className="form-group">
                                    <label className="mb-2 d-block">Marcadores por faixa de valor</label>
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary btn-block"
                                        onClick={() => this.setState({ modalRangesOpen: true })}
                                        title="Define cores/imagens de marcador por faixas de área, preço, oferta ou padrão"
                                    >
                                        <i className="fas fa-palette mr-1"></i> Criar Ranges
                                        {this.state.ranges.length > 0 && ` (${this.state.ranges.length})`}
                                    </button>
                                </div>

                                <hr />

                                <div className="form-group">
                                    <label className="mb-2 d-block">Raio por Deslocamento</label>
                                    <button
                                        type="button"
                                        className={`btn btn-block ${this.state.pickingIsochroneCenter ? 'btn-primary' : 'btn-outline-primary'}`}
                                        onClick={this.toggleIsochronePick}
                                        title="Desenha áreas alcançáveis a pé, de bicicleta ou de carro a partir de um ponto do mapa"
                                    >
                                        <i className="fas fa-clock mr-1"></i>
                                        {this.state.pickingIsochroneCenter ? 'Clique no mapa… (cancelar)' : 'Raio por Deslocamento'}
                                    </button>
                                </div>

                                <hr />

                                <div className="form-group">
                                    <label className="mb-2 d-block">Arquivos de mapa (KML)</label>
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary btn-block"
                                        onClick={this.handleDownloadKml}
                                        disabled={this.state.loadingKml}
                                        title="Exporta formas desenhadas e empreendimentos como KML"
                                    >
                                        <i className="fas fa-download mr-1"></i> Exportar KML
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary btn-block mt-2"
                                        onClick={() => this.kmlInputRef.current?.click()}
                                        disabled={this.state.loadingKml}
                                        title="Importa polígonos de um arquivo KML ou KMZ"
                                    >
                                        <i className="fas fa-upload mr-1"></i> Importar KML/KMZ
                                    </button>
                                    <input
                                        ref={this.kmlInputRef}
                                        type="file"
                                        accept=".kml,.kmz"
                                        style={{ display: 'none' }}
                                        onChange={(e) => {
                                            this.handleImportKmlFile(e.target.files?.[0]);
                                            e.target.value = '';
                                        }}
                                    />
                                    {this.state.loadingKml && (
                                        <small className="text-muted d-block mt-2">Processando arquivo…</small>
                                    )}
                                </div>
                            </div>
                        </Tab>

                        <Tab eventKey="legendas" title="Legenda">
                            <div
                                className="card-body"
                                style={{ height: '75vh', overflowY: 'auto' }}
                                id="legendas-container"
                            >
                                {this.state.showZoneamento && this.state.legendaZoneamento.length > 0 && (
                                    <LegendaDataLayer legendas={this.state.legendaZoneamento} />
                                )}
                                {this.state.showLegendaRenda && (
                                    <RendaIbgeLegend
                                        openModalLegendas={this.openModalLegendaSetores}
                                        colorRanges={this.state.color_ranges[this.getColorRangeKey()]}
                                        title={this.state.datalayerFriendlyName}
                                        datalayerName={this.state.datalayerName}
                                        datalayerColorProperty={this.state.datalayerColorProperty}
                                    />
                                )}
                                {this.state.showPolosGeradores && this.state.polosLoaded ? (
                                    <LegendaPoloGerador />
                                ) : this.state.showHeatMap ? (
                                    <HeatMapLegend
                                        weightType={this.state.heatMapWeightType}
                                        config={this.state.heatMapLegendConfig}
                                    />
                                ) : totalEmpreendimentos > 0 ? (
                                    <Legenda ranges={this.state.ranges} />
                                ) : !this.state.showZoneamento && !this.state.showLegendaRenda ? (
                                    <p className="text-muted small">
                                        Faça uma busca para visualizar a legenda.
                                    </p>
                                ) : null}
                            </div>
                        </Tab>
                    </Tabs>
                </Sidebar>

                {searchAreas.length > 0 && !right_panel_visible && (
                    <button
                        className="btn mapbox-right-panel-toggle"
                        title="Mostrar Resultados"
                        onClick={() => this.setState({ right_panel_visible: true })}
                    >
                        <i className="fas fa-chart-bar mr-1"></i>
                        <span className="mapbox-right-panel-toggle-badge">{searchAreas.length}</span>
                    </button>
                )}

                <Sidebar
                    visible={right_panel_visible}
                    position="right"
                    onHide={() => this.setState({ right_panel_visible: false })}
                    modal={false}
                    dismissable={false}
                    className="mapbox-right-panel"
                >
                    <div className="mapbox-right-panel-header">
                        <strong>Resultados da Análise</strong>
                        <span className="badge badge-secondary ml-2">{searchAreas.length}</span>
                    </div>
                    <div className="mapbox-right-panel-body">
                        <MapboxResultsPanel searchAreas={searchAreas} onRemoveArea={this.handleRemoveArea} />
                    </div>
                </Sidebar>

                {this.state.empreendimentoDetail && (
                    <EmpreendimentoModal
                        show={this.state.showEmpreendimentoModal}
                        empreendimento={this.state.empreendimentoDetail}
                        closeModalEmpreendimento={this.closeEmpreendimentoModal}
                        togglePainelEmpreendimento={this.closeEmpreendimentoModal}
                    />
                )}

                <MapCapture
                    visible={this.state.show_map_capture}
                    handleClose={this.closeMapCapture}
                    imageData={this.state.mapCaptureData}
                    canvas={this.state.mapCaptureCanvas}
                />

                <HeatMapModal
                    show={this.state.modalHeatMapOpen}
                    closeModal={() => this.setState({ modalHeatMapOpen: false })}
                    addHeatMap={this.addHeatMap}
                />

                <ShapeAttributesEditor
                    show={this.state.shapeEditorOpen}
                    shape={this.state.shapeEditing}
                    onHide={this.closeShapeEditor}
                    onApply={(attrs) => this.applyShapeAttributes(this.state.shapeEditing, attrs)}
                />

                <PolosGeradoresTypesModal
                    show={this.state.polosModalOpen}
                    closeModal={() => this.setState({ polosModalOpen: false })}
                    onApply={this.applyPolosTypes}
                    selected={this.state.polosTypesSelected}
                />

                <RaioDeslocamentoModal
                    show={this.state.showRaioDeslocamento}
                    center={this.state.isochroneCenter}
                    onHide={() => this.setState({ showRaioDeslocamento: false })}
                    onIsochronesReady={this.handleIsochronesReady}
                />

                <ModalRanges
                    show={this.state.modalRangesOpen}
                    ranges={this.state.ranges}
                    closeModal={() => this.setState({ modalRangesOpen: false })}
                    addRange={this.addRange}
                    updateRange={this.updateRange}
                    removeRange={this.removeRange}
                    replaceMarkers={this.applyMarkerRanges}
                />

                <ModalSalvarProjeto
                    show={this.state.modalSalvarProjetoOpen}
                    handleClose={() => this.setState({ modalSalvarProjetoOpen: false, projectErrors: {} })}
                    project_name={this.state.projectName}
                    handleNameProjectChanged={(e) => this.setState({ projectName: e.target.value })}
                    share_users={this.state.projectShareUsers}
                    handleShareUsersChanged={(users) => this.setState({ projectShareUsers: users || [] })}
                    errors={this.state.projectErrors}
                    loading={this.state.savingProject}
                    saveProject={this.saveProject}
                />

                <LinkagesDataDictionaryModal
                    visible={this.state.show_modal_data_dictionary}
                    onHide={() => this.setState({ show_modal_data_dictionary: false })}
                    activeDatalayer={this.state.datalayerName}
                />

                {this.state.show_modal_datalayer_datatable && (
                    <LinkagesDataTableModal
                        visible={this.state.show_modal_datalayer_datatable}
                        onHide={this.closeModalDatalayerTable}
                        activeDatalayer={this.state.datalayerName}
                        overlays={this.overlaysForDatalayerTable()}
                        data={this.state.datalayers_table_data}
                        getTransformedPayload={() => this.buildSensoPayload()}
                    />
                )}

                <ModalLegendaRendaIbge
                    show={this.state.show_modal_customizar_cores_setores}
                    handleClose={this.closeModalLegendaSetores}
                    colorRanges={this.state.color_ranges[this.getColorRangeKey()]}
                    updateRange={this.updateRendaColorRange}
                    replaceRanges={this.replaceColorRanges}
                    removeRange={this.removeRendaColorRange}
                    handleAplyColorRanges={this.handleAplyColorRanges}
                    colorOptions={this.state.colorsOptions}
                    datalayerName={this.state.datalayerFriendlyName}
                    datalayerColorProperty={this.state.datalayerColorProperty}
                    datalayerFillOpacity={this.state.datalayerFillOpacity}
                    onChangeOpacidadePreenchimento={this.onChangeOpacidadePreenchimento}
                />

                <ToastContainer position="top-right" autoClose={3500} />
            </>
        );
    }
}

export default MapboxMap;

if (document.getElementById('mapbox-map')) {
    ReactDOM.render(<MapboxMap />, document.getElementById('mapbox-map'));
}
